import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectItem,
} from "@components/ui/select";

import { useQuery } from "react-query";
import {
  getActionFrontend,
  getPluginsWithAction,
  getActionDropdown,
  getMeshPluginTemplate,
} from "@services/api/amlAnomalyDetection";
import PluginEnv from "../plugins/PluginEnvironment";
import { History } from "lucide-react";
import DataTable from "@components/common/DataTable";
import { EpochToDateTime } from "@utils/utils";
import { isFeatureAllowed, ALL_KEYS } from "@utils/licenseUtils";
import { Input } from "@components/ui/input";
import { useToast } from "@components/ui/use-toast";
import { Button } from "@components/ui/button";
import { Loader } from "lucide-react";
import { fetchLogicPageInfo, sendRequest } from "../../plugin/createPluginApi";
import { DefinedVariable } from "../../plugin/allData";
import { useCustomToast } from "@components/common/CustomToast";

interface PluginActionProps {
  show: boolean;
  onClose: () => void;
  actionData?: any;
  anmlId: number;
  anomalyType: string;
  anomalyTime: number;
  fileData?: any;
  entityData?: any;
}
const ENTITY_CATEGORY_MAP: Record<string, string[]> = {
  ip: ["ANOMALOUS_IP", "DESTINATION_IP", "SIP", "DIP", "IP"],
  mac: ["ANOMALOUS_IP_MAC", "SMAC", "DMAC"],
  username: ["USERNAME"],
  port: ["DESTINATION_PORT"],
  time: ["TIME"],
  domain: ["QUERY", "HOST", "SERVER_NAME", "DOMAIN"],
  email: ["USERNAME"],
  url: ["URL"],
  filename: ["FILENAME"],
  hash: ["HASH", "MD5", "SHA256", "SHA1"],
  number: ["SCORE"],
  integer: ["SCORE"],
  host: ["ANOMALOUS_HOST", "HOSTNAME", "HOST_NAME", "ANOMALOUS_HOSTNAME"],
  string: [],
};

const getCanonicalEntityType = (value: string): string => {
  const key = String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_\-\s]+/g, "");

  if (["ip", "ipaddress"].includes(key)) return "ip";
  if (["mac", "macaddress"].includes(key)) return "mac";
  if (["domain", "fqdn", "dns"].includes(key)) return "domain";
  if (["host", "hostname"].includes(key)) return "host";
  if (["user", "username"].includes(key)) return "username";
  if (["integer", "number"].includes(key)) return "number";

  return String(value || "")
    .toLowerCase()
    .trim();
};

const getAnomalyTypeKeysFromField = (field: any): string[] => {
  const rawTypeTokens = (field?.values || [])
    .flatMap((item: any) => [item?.value, item?.displayName])
    .filter(
      (v: any) => v !== undefined && v !== null && String(v).trim() !== "",
    )
    .flatMap((token: any) => String(token).split(/[;,|/]/g))
    .map((token: string) => token.trim())
    .filter(Boolean);

  return Array.from(
    new Set(
      rawTypeTokens.map((token: string) => getCanonicalEntityType(token)),
    ),
  );
};

const safeJsonParse = (value: any): any => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const collectAnomalyEntityEntries = (
  entityData: any,
  anomalyData: any,
): Array<[string, string]> => {
  const merged = new Map<string, string>();

  const addEntries = (obj: any) => {
    if (!obj || typeof obj !== "object") return;
    Object.entries(obj).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      const text = String(value).trim();
      if (!text) return;
      const dedupeKey = `${String(key)}::${text}`;
      if (!merged.has(dedupeKey)) {
        merged.set(dedupeKey, text);
      }
    });
  };

  addEntries(entityData);
  addEntries(anomalyData);

  const events = anomalyData?.newData;
  if (Array.isArray(events)) {
    events.forEach((ev: any) => {
      addEntries(ev);

      const eventPayload = safeJsonParse(ev?.EventData);
      addEntries(eventPayload);

      const nested = eventPayload?.data;
      addEntries(nested);
      addEntries(nested?.display);
    });
  }

  return Array.from(merged.keys()).map((combinedKey) => {
    const [key, ...rest] = combinedKey.split("::");
    return [key, rest.join("::")];
  });
};

function buildPluginActionRawData(anomaly: any, evnt?: any) {
  if (!anomaly || !Object.keys(anomaly).length) return null;

  if (!evnt) {
    const ip = anomaly.anomalous_ip || "";
    return {
      ip,
      sip: ip,
      smac: anomaly.anomalous_ip_mac || "",
      hostname: anomaly.anomalous_hostname,
      anomalous_host: anomaly.anomalous_hostname,
      anomalous_ip: ip,
      anomaly_id: anomaly.anomaly_id || "",
      anomalyData: anomaly,
    };
  }

  const evData = JSON.parse(evnt.EventData);
  let dataKey = "data";
  if (!("data" in evData) && "Data" in evData) {
    dataKey = "Data";
  }
  const nested = evData[dataKey] || {};
  const events = JSON.parse(anomaly?.data || "{}")?.events;
  const sip =
    evData.sip ||
    nested.sip ||
    anomaly.anomalous_ip ||
    evData.anomalous_ip ||
    "";

  return {
    ip: sip,
    sip,
    smac: evData.smac || nested.smac || anomaly.anomalous_ip_mac || "",
    dmac: evData.dmac || nested.dmac || "",
    hostname: anomaly.anomalous_hostname,
    anomalous_host: anomaly.anomalous_hostname,
    anomalous_ip: evData.anomalous_ip || anomaly.anomalous_ip,
    anomaly_type: evnt.EventName,
    time_str: evData?.time_seen,
    GlobalID: evData?.GlobalID || anomaly?.GlobalID,
    dip: evData.dip || nested.dip || anomaly.dip,
    data: nested,
    username: evData.AnomalouUser,
    desc: evnt?.desc || "",
    events: events || [],
    anomaly_id: anomaly?.anomaly_id || "",
    anomalyData: anomaly,
    ...nested,
  };
}
const PluginActionDialog: React.FC<PluginActionProps> = React.memo(
  ({
    show,
    onClose,
    anmlId,
    anomalyType,
    anomalyTime,
    fileData,
    entityData,
  }) => {
    const [showHistory, setShowHistory] = React.useState(false);
    const [pluginData, setPluginData] = React.useState<any>({});
    const [pluginDropdown, setPluginDropdown] = React.useState<any>([]);
    const [meshPluginsDropdown, setMeshPluginsDropdown] = React.useState<any>(
      [],
    );
    const [pluginContent, setPluginContent] = React.useState<string>("");
    const [pluginHistory, setPluginHistory] = React.useState<any>([]);
    const [allHistory, setAllHistory] = React.useState<any>([]);
    const [allActionData, setAllActionData] = React.useState<any>({});
    const [selectedEvent, setSelectedEvent] = React.useState<string>("");
    const [isMeshconntexSelected, setIsMeshconntexSelected] =
      React.useState(false);
    const [meshPluginId, setMeshPluginId] = React.useState<string>("");
    const [meshPluginUid, setMeshPluginUid] = React.useState<string>("");
    const [loading, setLoading] = React.useState(false);

    const [formValues, setFormValues] = React.useState<Record<string, any>>({});
    const [dropdownChains, setDropdownChains] = React.useState<{
      [key: number]: { [key: string | number]: string };
    }>({});
    const [definedVariables, setDefinedVariables] = React.useState<
      DefinedVariable[]
    >([]);
    const definedVariablesRef = React.useRef<DefinedVariable[]>([]);

    // Add these state variables after your existing states
    const [runtimeDropdownData, setRuntimeDropdownData] = React.useState<{
      [key: string]: any[];
    }>({});

    const [loadingDropdowns, setLoadingDropdowns] = React.useState<{
      [key: string]: boolean;
    }>({});

    // Track in-flight API requests to prevent duplicates
    const inFlightRequests = React.useRef<Set<string>>(new Set());

    // Track active API call count for loading state
    const activeApiCallsCount = React.useRef<number>(0);

    // Guard against recursive variable refresh loops
    const inFlightVariableRefresh = React.useRef<Set<string>>(new Set());

    // Track dynamic options for config dropdown targetFields
    const [configTargetFieldData, setConfigTargetFieldData] = React.useState<{
      [configValue: string]: {
        [fieldName: string]: {
          [selectedOption: string]: any[];
        };
      };
    }>({});

    const [anomalyFieldSelections, setAnomalyFieldSelections] = React.useState<
      Record<string, string>
    >({});

    const meshVersion =
      pluginData?.mesh_version ||
      pluginData?.meshVersion ||
      pluginData?.details?.mesh_version ||
      pluginData?.details?.meshVersion ||
      meshPluginsDropdown?.data?.mesh_version ||
      meshPluginsDropdown?.data?.meshVersion ||
      "";
    const isMeshV2 = String(meshVersion).startsWith("2");

    const { showToast } = useCustomToast();

    const encodeStringify: any = (obj: any) => {
      return encodeURIComponent(JSON.stringify(obj));
    };
    const { toast } = useToast();
    const [base64EncData, setBase64EncData] = React.useState<string>("");
    React.useEffect(() => {
      const isMesh =
        pluginData?.is_meshconnectx === 1 && pluginData?.action === true;
      if (Object.keys(pluginData).length > 0 && !isMesh) {
        setTimeout(() => {
          dataRefetch().then((data: any) => {
            if (!data?.data) return;
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = data.data;
            tempDiv.querySelectorAll("select, input").forEach((select: any) => {
              select.style.backgroundColor = "var(--background) !important";
              select.style.color = "var(--foreground) !important";
              select.style.border = "1px solid var(--secondary) !important";
            });

            tempDiv.querySelectorAll(".btn-login").forEach((btn: any) => {
              btn.style.backgroundColor = "var(--primary) !important";
              btn.style.color = "var(--background) !important";
            });
            setPluginContent(tempDiv.innerHTML);
          });
        }, 0);
      }
    }, [pluginData, base64EncData]);
    const { data: actionData } = useQuery(
      ["getActionDropdown", anmlId],
      ({ signal }) =>
        getActionDropdown(
          { anomalyId: anmlId, anomalyType, anomalyTime },
          signal,
        ),
    );
    const { data: pluginActionData, isFetching: pluginLoading } = useQuery(
      ["getPluginsWithAction"],
      ({ signal }) => getPluginsWithAction(anomalyType, signal),
    );
    const { refetch: fetchMeshTemplate, isFetching: meshPluginLoading } =
      useQuery(
        ["getMeshPluginTemplate", meshPluginId, meshPluginUid],
        ({ signal }) =>
          getMeshPluginTemplate(meshPluginId, meshPluginUid, signal),
        { enabled: false },
      );
    const { refetch: dataRefetch } = useQuery(
      ["getActionFrontend", pluginData.id],
      () =>
        getActionFrontend(
          "/plugin/" +
            pluginData.id +
            "/template/" +
            pluginData.details.template +
            "?templateFunction=get_plugin_action_frontend_template",
          {
            template: pluginData.details.template,
            actionId: pluginData.details.action_id,
            templateFunction: "get_plugin_action_frontend_template",
          },
        ),
      { enabled: false },
    );

    React.useEffect(() => {
      if (pluginActionData) {
        const pdata: any = [];
        pluginActionData.data
          .filter((element: any) => {
            // If is_meshconnectx is 1, only include if action is true
            if (element.is_meshconnectx === 1) {
              return element.action === true;
            }
            // Otherwise, include all non-meshconnectx plugins
            return true;
          })
          .forEach((element: any) => {
            if (element.action && element.is_meshconnectx === 1) {
              pdata.push(
                <SelectItem key={element.id} value={JSON.stringify(element)}>
                  {element.name} (MeshConnectX)
                </SelectItem>,
              );
            } else {
              pdata.push(
                <SelectItem key={element.id} value={JSON.stringify(element)}>
                  {element.name}
                </SelectItem>,
              );
            }
          });
        setAllHistory(pluginActionData.prevActions);
        setPluginDropdown(pdata);
      }
    }, [pluginActionData]);

    React.useEffect(() => {
      if (actionData) {
        const anomaly = actionData.anomalies?.[0] || {};
        setAllActionData(anomaly);
        let rawData: Record<string, any> | null = null;
        if (isFeatureAllowed(ALL_KEYS.PLUGIN_ACTION_EVENTS)) {
          const evnt = anomaly?.newData?.[0];
          if (evnt) {
            setSelectedEvent(evnt.EventId);
            rawData = buildPluginActionRawData(anomaly, evnt);
          }
        }
        if (!rawData) {
          rawData = buildPluginActionRawData(anomaly);
        }
        if (rawData) {
          setBase64EncData(encodeStringify(rawData));
        }
      }
      if (fileData && isFeatureAllowed(ALL_KEYS.PLUGIN_ACTION_FILE)) {
        const encodedStr = encodeStringify(fileData || {});
        setBase64EncData(encodedStr);
      }
    }, [actionData]);

    async function changePlugin(e: string) {
      if (!e || e === "select") {
        setPluginData({});
        setPluginContent("");
        setPluginHistory([]);
        setIsMeshconntexSelected(false);
        setMeshPluginId("");
        setMeshPluginUid("");
        setMeshPluginsDropdown([]);
        setFormValues({});
        setDropdownChains({});
        return;
      }

      const pdata = JSON.parse(e);
      const pluginId = pdata.id;
      const pluginUid = pdata.details?.template || "";
      const isMesh = pdata.is_meshconnectx === 1 && pdata.action === true;
      setIsMeshconntexSelected(isMesh);

      const temp: any = [];
      allHistory.map((item: any) => {
        if (item.pluginId == pluginId) {
          let desc: string = "";
          Object.keys(item.postData).map((key) => {
            desc += key + ": " + item.postData[key] + ", ";
          });

          temp.push({
            plugin: item.pluginName,
            action: item.action,
            time: EpochToDateTime(item.time),
            status: item.status,
            misc: item.userId,
            desc: desc,
          });
        }
      });

      setPluginHistory(temp);
      setPluginContent("");
      setPluginData(pdata);
      if (isMesh) {
        setMeshPluginId(pluginId);
        setMeshPluginUid(pluginUid);
        setIsMeshconntexSelected(true);

        try {
          const { data } = await getMeshPluginTemplate(pluginId, pluginUid);
          setMeshPluginsDropdown(data || []);
        } catch (err) {
          console.error(" Mesh plugin fetch failed:", err);
        }
      } else {
        setIsMeshconntexSelected(false);
      }
    }
    React.useEffect(() => {
      if (isMeshconntexSelected && meshPluginId && meshPluginUid) {
        (async () => {
          try {
            const { data } = await fetchMeshTemplate();
            setMeshPluginsDropdown(data || []);
          } catch (err) {
            console.error("Mesh plugin fetch failed:", err);
          }
        })();
      }
    }, [isMeshconntexSelected, meshPluginId, meshPluginUid]);

    const changeEvent = (e: string) => {
      if (!e || e === "select") {
        setSelectedEvent("");
        const rawData = buildPluginActionRawData(allActionData);
        if (rawData) {
          setBase64EncData(encodeStringify(rawData));
        }
        return;
      }

      setSelectedEvent(e);
      const evnt = allActionData?.newData?.find((ev: any) => ev.EventId == e);
      if (evnt) {
        const rawData = buildPluginActionRawData(allActionData, evnt);
        if (rawData) {
          setBase64EncData(encodeStringify(rawData));
        }
      }
    };

    const historyColumns = [
      { header: "Plugin", accessor: "plugin" },
      { header: "Action", accessor: "action" },
      { header: "Time", accessor: "time" },
      { header: "Status", accessor: "status" },
      { header: "Misc", accessor: "misc" },
      { header: "", accessor: "desc" },
    ];

    // const handleFieldAction = (field: any, value?: string) => {
    //   const trigger = field?.userAction?.trigger;

    //   if (trigger === "onLoad") {
    //     console.log(`OnLoad triggered for ${field.givenName}`);
    //   }

    //   if (trigger === "onSelect") {
    //     console.log(
    //       `OnSelect triggered for ${field.givenName} with value:`,
    //       value,
    //     );
    //   }
    // };

    // Fetch logic page info for authentication variable refresh
    const { data: logicPageInfo } = useQuery({
      queryKey: ["fetchLogicPageData", meshPluginId],
      queryFn: () => fetchLogicPageInfo(meshPluginId ?? ""),
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      enabled: !!meshPluginId,
    });

    function interpolateDefinedVariablesOnly(
      input: string,
      definedVars: DefinedVariable[] = [],
    ): string {
      if (!input || typeof input !== "string") return input;

      return input.replace(/{{\s*(.*?)\s*}}/g, (match, key) => {
        // Skip config variables
        if (key.startsWith("config.")) return match;

        // Handle array-style access: values[1].id
        const arrayMatch = key.match(/^(\w+)\[(\d+)\]\.(\w+)$/);
        if (arrayMatch) {
          const [, arrayName, indexStr, prop] = arrayMatch;
          const variable = definedVars.find((v) => v.name === arrayName);
          if (!variable || !Array.isArray(variable.value)) return match;

          const index = parseInt(indexStr, 10);
          const item = variable.value[index];
          if (!item || !(prop in item)) return match;

          return String(item[prop]);
        }

        // Simple variable (no array index)
        const found = definedVars.find((v) => v.name === key);
        return found?.value != null ? String(found.value) : match;
      });
    }

    const extractTemplateVariableNames = (input: string): string[] => {
      if (!input || typeof input !== "string") return [];

      const matches = input.match(/{{\s*([^{}]+?)\s*}}/g) || [];
      const names = matches
        .map((match) => match.replace(/{{\s*|\s*}}/g, "").trim())
        .map((raw) => raw.split(".")[0].split("[")[0].trim())
        .filter(Boolean)
        .filter(
          (name) =>
            !name.startsWith("config") &&
            !name.startsWith("data") &&
            !name.startsWith("anomaly") &&
            !name.startsWith("action") &&
            !name.startsWith("$"),
        );

      return [...new Set(names)];
    };

    const collectRequestTemplateVariables = (
      requestDataItem: any,
    ): string[] => {
      if (!requestDataItem) return [];

      const texts: string[] = [];

      if (typeof requestDataItem.url === "string")
        texts.push(requestDataItem.url);
      if (typeof requestDataItem.endpoint === "string")
        texts.push(requestDataItem.endpoint);
      if (typeof requestDataItem.body === "string")
        texts.push(requestDataItem.body);
      if (
        requestDataItem.body &&
        typeof requestDataItem.body === "object" &&
        !Array.isArray(requestDataItem.body)
      ) {
        texts.push(JSON.stringify(requestDataItem.body));
      }

      if (Array.isArray(requestDataItem.headers)) {
        requestDataItem.headers.forEach((h: any) => {
          if (typeof h?.key === "string") texts.push(h.key);
          if (typeof h?.value === "string") texts.push(h.value);
        });
      }

      if (requestDataItem.auth && typeof requestDataItem.auth === "object") {
        if (typeof requestDataItem.auth.token === "string")
          texts.push(requestDataItem.auth.token);
        if (typeof requestDataItem.auth.username === "string")
          texts.push(requestDataItem.auth.username);
        if (typeof requestDataItem.auth.password === "string")
          texts.push(requestDataItem.auth.password);
      }

      if (Array.isArray(requestDataItem.queryParams)) {
        requestDataItem.queryParams.forEach((q: any) => {
          if (typeof q?.key === "string") texts.push(q.key);
          if (typeof q?.value === "string") texts.push(q.value);
        });
      }

      return [...new Set(texts.flatMap(extractTemplateVariableNames))];
    };

    const hasResolvedValue = (value: any): boolean => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string") {
        if (!value.trim()) return false;
        return !/{{\s*[^{}]+\s*}}/.test(value);
      }
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") return Object.keys(value).length > 0;
      return true;
    };

    const getCurrentDefinedVariables = (): DefinedVariable[] =>
      definedVariablesRef.current?.length
        ? definedVariablesRef.current
        : definedVariables;

    // mutate the all logics using the meshPluginId
    const { data: fetchedLogicPageData } = useQuery({
      queryKey: ["fetchLogicPageData", meshPluginId],
      queryFn: () => fetchLogicPageInfo(meshPluginId!),
      refetchOnWindowFocus: false,
      enabled: Boolean(meshPluginId),
    });

    // Extract action logics when fetchedLogicPageData changes
    React.useEffect(() => {
      if (fetchedLogicPageData?.data?.LogicBundles) {
        // const actions = fetchedLogicPageData.data.LogicBundles.filter(
        //   (bundle: any) => bundle.bundleType === "action",
        // );

        const allDefinedVars = fetchedLogicPageData?.data?.variables || [];
        definedVariablesRef.current = allDefinedVars;
        setDefinedVariables(allDefinedVars);
      }
    }, [fetchedLogicPageData]);

    React.useEffect(() => {
      definedVariablesRef.current = definedVariables;
    }, [definedVariables]);

    const Meshfields = React.useMemo(() => {
      if (!meshPluginsDropdown?.data) return [];

      const uiForm = meshPluginsDropdown.data?.ui_data?.forms?.[0]?.fieldList;
      const configList = Object.values(
        meshPluginsDropdown.data?.config_data || {},
      )[0];

      let fieldList: any[] = [];
      if (Array.isArray(uiForm)) fieldList = uiForm;
      else if (Array.isArray(configList)) fieldList = configList;
      else fieldList = [];

      return fieldList.map((field: any) => {
        const isAnomaly = field.isAnomaly === true;
        const anomalyKey = field.values?.[0]?.value;
        let dropdownValues = field.values ?? [];

        if (field.isSettings) {
          const configData = meshPluginsDropdown.data?.config_data || {};
          const firstKey = Object.keys(configData)?.[0];
          const configArray = configData[firstKey] || [];

          dropdownValues = configArray.map((conf: any) => ({
            displayName: conf.title,
            value: conf.uid,
          }));
        }

        if (isAnomaly && anomalyKey && entityData) {
          const formattedKey = anomalyKey.toUpperCase();

          const entityValue =
            entityData?.[formattedKey] ||
            entityData?.[anomalyKey] ||
            entityData?.[`${formattedKey}_addr`] ||
            entityData?.[`${anomalyKey}_addr`] ||
            entityData?.[`${formattedKey}_address`] ||
            entityData?.[`${anomalyKey}_address`] ||
            entityData?.[`d${formattedKey}`] ||
            entityData?.[`s${formattedKey}`] ||
            entityData?.[`d${anomalyKey}`] ||
            entityData?.[`s${anomalyKey}`];

          if (Array.isArray(entityValue)) {
            dropdownValues = entityValue
              .filter((v) => v && String(v).trim() !== "")
              .map((v: any) => ({
                displayName: `${formattedKey} - ${v}`,
                value: `${formattedKey}-${v}`,
              }));
          } else if (entityValue) {
            dropdownValues = [
              {
                displayName: `${formattedKey} - ${entityValue}`,
                value: `${formattedKey}-${entityValue}`,
              },
            ];
          } else {
            dropdownValues = [];
          }
        }

        const defaultValue =
          isAnomaly && dropdownValues.length > 0 ? dropdownValues[0].value : "";

        return { ...field, dropdownValues, defaultValue };
      });
    }, [meshPluginsDropdown, entityData]);

    //  Handle field value change
    const handleFieldChange = (fieldId: string, value: any) => {
      setFormValues((prev) => ({
        ...prev,
        [fieldId]: value,
      }));

      const field = Meshfields.find((f: any) => f.fieldId === fieldId);
      if (field?.isAnomaly && field?.values?.[0]?.value) {
        const anomalyKey = field.values[0].value;
        setAnomalyFieldSelections((prev) => ({
          ...prev,
          [anomalyKey]: value,
        }));
      }
    };

    // Interpolate only anomaly variables in the input string
    function interpolateAnomalyVariables(
      input: string,
      anomalySelections: Record<string, string>,
    ): string {
      if (!input || typeof input !== "string") return input;

      // Replace {{anomaly.xxx}} patterns with actual selected values
      return input.replace(/{{\s*anomaly\.(\w+)\s*}}/g, (match, key) => {
        const value = anomalySelections[key];
        return value !== undefined ? String(value) : match;
      });
    }

    //  Handle dependent target field change
    const handleDependedentTargetFieldChange = (
      val: any,
      targetFieldKey: string,
      fieldIndex: number | string,
      level: number,
      targetIdx: number,
    ) => {
      // Skip placeholder
      if (val === "select" || val === "") return;

      // Update dropdown chains
      setDropdownChains?.((prev) => {
        const updated = { ...prev };
        if (!updated[Number(fieldIndex)]) {
          updated[Number(fieldIndex)] = {};
        }
        updated[Number(fieldIndex)][`target-${level}-${targetIdx}`] = val;

        // Clear subsequent target fields at this level and beyond
        Object.keys(updated[Number(fieldIndex)]).forEach((key) => {
          if (key.startsWith(`target-`)) {
            const [, keyLevel, keyIdx] = key.split("-").map(Number);
            if (
              keyLevel > level ||
              (keyLevel === level && keyIdx > targetIdx)
            ) {
              delete updated[Number(fieldIndex)][key];
            }
          }
        });

        return updated;
      });

      // Update form values
      handleFieldChange(targetFieldKey, val);
    };

    //  Handle children dropdown change
    const childrenDropdownChange = (
      fieldIndex: number | string,
      level: number,
      val: any,
    ) => {
      if (val === "select" || val === "") return;

      setDropdownChains?.((prev) => {
        const prevChain = prev?.[Number(fieldIndex)] || {};
        const updated = { ...prevChain };
        updated[level] = val;

        // Clear subsequent levels
        Object.keys(updated).forEach((key) => {
          const keyNum = parseInt(key);
          if (!isNaN(keyNum) && keyNum > level) {
            delete updated[key];
          }
          if (
            key.startsWith(`target-`) &&
            parseInt(key.split("-")[1]) >= level
          ) {
            delete updated[key];
          }
        });
        return { ...prev, [fieldIndex]: updated };
      });

      handleFieldChange(`${fieldIndex}-${level}`, val);
    };

    //////////////////////////////////////

    //  Recursive rendering of dropdown tree levels (same as ActionsPage)
    const renderDropdownTreeLevels = (
      node: any,
      fieldIndex: number | string,
      level: number,
      parentChain: string[] = [],
    ): React.ReactNode => {
      if (!node) return null;

      const getTargetFieldLabel = (targetField: any): string =>
        String(
          targetField?.fieldName ||
            targetField?.givenName ||
            targetField?.variableName ||
            "",
        ).trim();

      const isRenderableTargetField = (targetField: any): boolean =>
        getTargetFieldLabel(targetField) !== "";

      //  First priority: Render targetFields if they exist
      if (node.targetFields && node.targetFields.length > 0) {
        const filteredTargetFields = node.targetFields.filter(
          isRenderableTargetField,
        );
        if (filteredTargetFields.length === 0) return null;

        // Determine if this is a config dropdown
        const parentField = Meshfields[Number(fieldIndex)];
        const isConfigField = parentField?.isConfig;
        const configValue = formValues[parentField?.fieldId] || "";
        return (
          <div key={`${node.value}-targetFields-${level}`} className="mt-2">
            {filteredTargetFields.map((targetField: any, targetIdx: number) => {
              const targetFieldKey = `${fieldIndex}-${level}-${targetIdx}`;

              // Check if we have runtime data or use preview/default options
              const targetLoadingKey =
                targetField.sourceType === "bundle" &&
                targetField.bundleId &&
                targetField.variableName
                  ? `${targetField.bundleId}-${targetField.variableName}-${targetField.requestId || 1}`
                  : "";
              const targetIsLoading = targetLoadingKey
                ? loadingDropdowns[targetLoadingKey]
                : false;

              const targetSelectedValue =
                dropdownChains?.[Number(fieldIndex)]?.[
                  `target-${level}-${targetIdx}`
                ] || "";

              // Build options from runtime data or fallback to preview/default options
              let targetOptions: any[] = [];

              // Priority 1: For config dropdowns, check dynamic data first
              if (isConfigField && configValue) {
                // For nested targetFields, we need to look up by parent selection
                // For first-level targetFields, use configValue as the key
                let lookupKey = configValue;

                // If this is a nested field (level > 1), find the parent's selected value
                if (level > 1) {
                  // For nested fields, look for the previous targetField's selection
                  const parentTargetKey = `target-${level - 1}-${targetIdx}`;
                  const parentValue =
                    dropdownChains?.[Number(fieldIndex)]?.[parentTargetKey];

                  if (parentValue) {
                    lookupKey = parentValue;
                  }
                }

                const dynamicOptions =
                  configTargetFieldData[configValue]?.[targetField.fieldName]?.[
                    lookupKey
                  ];

                if (dynamicOptions && Array.isArray(dynamicOptions)) {
                  targetOptions = dynamicOptions.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    _rawData: {
                      ...item,
                      // targetFields already attached from fetchConfigTargetFieldOptions
                      targetFields: item.targetFields || [],
                    },
                  }));
                }
              }

              // Priority 2: Try to get data from runtimeDropdownData for regular dropdowns
              if (targetOptions.length === 0) {
                const runtimeKey = `${fieldIndex}-1-${targetField.variableName}`;
                const runtimeData = runtimeDropdownData[runtimeKey];

                if (
                  runtimeData &&
                  Array.isArray(runtimeData) &&
                  targetField.sourceType === "bundle"
                ) {
                  // Use ALL API response data - previewOptions is only a TEMPLATE for targetFields
                  // Get targetFields template from first previewOption (all items have same nested structure)
                  const targetFieldsTemplate =
                    targetField.previewOptions?.[0]?.targetFields || [];

                  targetOptions = runtimeData.map((item: any) => {
                    const label =
                      (targetField.labelKey && item[targetField.labelKey]) ||
                      item.label ||
                      item.name ||
                      item.title ||
                      item.displayName ||
                      String(item);
                    const value =
                      (targetField.valueKey && item[targetField.valueKey]) ||
                      item.value ||
                      item.id ||
                      item.uid ||
                      String(item);

                    return {
                      label: String(label),
                      value: String(value),
                      _rawData: {
                        ...item, // All API response data
                        // Apply targetFields template to ALL items (not just matching ones)
                        targetFields: targetFieldsTemplate,
                      },
                    };
                  });
                }
              }

              // Priority 3: Use defaultOptions ONLY for sourceType === "default" (not for bundle types)
              if (
                targetOptions.length === 0 &&
                targetField.sourceType === "default" &&
                targetField.defaultOptions &&
                Array.isArray(targetField.defaultOptions)
              ) {
                targetOptions = targetField.defaultOptions.map((opt: any) => ({
                  label: opt.label,
                  value: opt.value,
                  _rawData: {
                    ...opt,
                    targetFields: opt.targetFields || [],
                  },
                }));
              }

              return (
                <div key={targetFieldKey} className="mb-3">
                  <label className="block text-sm font-medium mb-1">
                    {getTargetFieldLabel(targetField)}
                    {targetIsLoading && (
                      <Loader
                        className="inline-block ml-2 animate-spin"
                        size={14}
                      />
                    )}
                  </label>
                  <Select
                    value={targetSelectedValue}
                    disabled={targetIsLoading}
                    onValueChange={async (val) => {
                      handleDependedentTargetFieldChange(
                        val,
                        targetFieldKey,
                        fieldIndex,
                        level,
                        targetIdx,
                      );

                      // If this is a config dropdown, check if we need to fetch nested targetFields
                      if (isConfigField && configValue) {
                        // Check selected option first
                        const selectedOption = targetOptions.find(
                          (opt: any) => opt.value === val,
                        );

                        // Determine if there are nested targetFields (from selected option or template)
                        let nestedTargetFields: any[] = [];

                        if (
                          selectedOption?._rawData?.targetFields?.length > 0
                        ) {
                          nestedTargetFields =
                            selectedOption._rawData.targetFields.filter(
                              isRenderableTargetField,
                            );
                        } else if (
                          targetOptions.length > 0 &&
                          targetOptions[0]?._rawData?.targetFields?.length > 0
                        ) {
                          // Use template structure from first option if available
                          nestedTargetFields =
                            targetOptions[0]._rawData.targetFields.filter(
                              isRenderableTargetField,
                            );
                        }

                        if (nestedTargetFields.length > 0) {
                          // Build parent selections context including current selection
                          const parentSelections: Record<string, string> = {
                            [getTargetFieldLabel(targetField)]: val,
                          };

                          // Fetch options for all nested targetFields
                          for (const nestedTargetField of nestedTargetFields) {
                            await fetchConfigTargetFieldOptions(
                              configValue,
                              val,
                              nestedTargetField,
                              Number(fieldIndex),
                              parentSelections,
                            );
                          }

                          // Force component re-render to show newly loaded nested dropdowns
                          setFormValues((prev) => ({
                            ...prev,
                            _refresh: Date.now(),
                          }));
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={
                          targetIsLoading
                            ? "Loading..."
                            : getTargetFieldLabel(targetField) ||
                              "Select an option"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {targetIsLoading ? (
                          <SelectItem value="loading" disabled>
                            Loading...
                          </SelectItem>
                        ) : targetOptions.length === 0 ? (
                          <SelectItem value="none" disabled>
                            No options available
                          </SelectItem>
                        ) : (
                          <>
                            <SelectItem value="select">
                              Select an option
                            </SelectItem>
                            {targetOptions.map((opt: any, idx: number) => (
                              <SelectItem key={idx} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectGroup>
                    </SelectContent>
                  </Select>

                  {/*  Recursively render nested targetFields - Show immediately after parent selection */}
                  {targetSelectedValue &&
                    targetSelectedValue !== "select" &&
                    (() => {
                      const selectedOption = targetOptions.find(
                        (opt: any) => opt.value === targetSelectedValue,
                      );

                      // Check if the selected option or ANY option has nested targetFields
                      // (for config dropdowns, structure comes from template, data from API)
                      let hasNestedFields = false;
                      let nodeToRender: any = null;

                      if (selectedOption?._rawData) {
                        hasNestedFields =
                          selectedOption._rawData.targetFields?.length > 0;
                        nodeToRender = selectedOption._rawData;
                      }

                      // For config dropdowns, ALWAYS check template structure even if no API data yet
                      if (isConfigField && !hasNestedFields) {
                        // Look at the template structure to see if nested fields exist
                        const templateOption = targetOptions[0]?._rawData;
                        const templateTargetFields =
                          templateOption?.targetFields?.filter(
                            isRenderableTargetField,
                          ) || [];
                        if (templateTargetFields.length > 0) {
                          hasNestedFields = true;
                          // Create a node with selected value but template structure
                          nodeToRender = {
                            label: selectedOption?.label || targetSelectedValue,
                            value: targetSelectedValue,
                            targetFields: templateTargetFields,
                          };
                        }
                      }

                      if (hasNestedFields && nodeToRender) {
                        return renderDropdownTreeLevels(
                          nodeToRender,
                          fieldIndex,
                          level + 1,
                          [...parentChain, targetSelectedValue],
                        );
                      }

                      return null;
                    })()}
                </div>
              );
            })}
          </div>
        );
      }

      // Second priority: Render children if they exist (old behavior for backward compatibility)
      if (node.children && node.children.length > 0) {
        const options = node.children.map((child: any) => ({
          label: child.label,
          value: child.value,
          _rawData: child,
        }));

        // // PURE API response
        // const options = finalValue.map((item) => ({
        //   label: item[targetField.labelKey],
        //   value: item[targetField.valueKey],
        //   ...item,
        //   targetFields: item.targetFields || [],
        // }));

        const selectedValue =
          dropdownChains?.[Number(fieldIndex)]?.[level] || "";

        return (
          <div key={`${node.value}-${level}`} className="mt-2">
            <label className="block text-sm font-medium mb-1">
              {node.target || `Level ${level + 1}`}
            </label>
            <Select
              value={selectedValue}
              onValueChange={(val) =>
                childrenDropdownChange(fieldIndex, level, val)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value="select">Select an option</SelectItem>
                  {options.map((opt: any) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            {/* Recurse into selected child */}
            {selectedValue &&
              selectedValue !== "select" &&
              (() => {
                const selectedOption = options.find(
                  (opt: any) => opt.value === selectedValue,
                );
                if (selectedOption?._rawData) {
                  return renderDropdownTreeLevels(
                    selectedOption._rawData,
                    fieldIndex,
                    level + 1,
                    [...parentChain, selectedValue],
                  );
                }
                return null;
              })()}
          </div>
        );
      }
      // No children and no targetFields - end of tree
      return null;
    };

    // Add this useEffect to clean up state when plugin changes
    React.useEffect(() => {
      if (meshPluginId) {
        // Reset all dynamic states when plugin changes
        setFormValues({});
        setDropdownChains({});
        setRuntimeDropdownData({});
        setLoadingDropdowns({});
      }
    }, [meshPluginId]);

    //  Handle form submission with real values
    // Update handleFormSubmit function (around line 830)

    const handleFormSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);

      try {
        if (isMeshconntexSelected) {
          // Find the submit field configuration
          const submitField = Meshfields.find(
            (field: any) => field.fieldType === "submit",
          );

          if (!submitField?.submitConfig) {
            toast({
              title: "Configuration Error",
              description: "No submit configuration found",
              variant: "destructive",
              duration: 3000,
            });
            setLoading(false);
            return;
          }

          let bundleId: string = "";
          let requestId: string = "";

          // Check if dependency mode (Ismultiple) is enabled
          if (submitField.submitConfig.Ismultiple) {
            // Find the dependency field
            const dependencyFieldName =
              submitField.submitConfig.submit_types?.[0]?.field_given_name;

            if (!dependencyFieldName) {
              toast({
                title: "Configuration Error",
                description: "Dependency field not configured",
                variant: "destructive",
                duration: 3000,
              });
              setLoading(false);
              return;
            }

            // Find the dependency field from Meshfields
            const dependencyField = Meshfields.find(
              (f: any) =>
                f.givenName === dependencyFieldName ||
                f.fieldTitle === dependencyFieldName,
            );

            if (!dependencyField) {
              toast({
                title: "Configuration Error",
                description: `Dependency field "${dependencyFieldName}" not found`,
                variant: "destructive",
                duration: 3000,
              });
              setLoading(false);
              return;
            }

            // Get the selected value from the dependency field
            const dependencyFieldIdx = Meshfields.findIndex(
              (f: any) => f.fieldId === dependencyField.fieldId,
            );
            const selectedValue = isMeshV2
              ? formValues[dependencyField.fieldId]
              : formValues[dependencyField.fieldId] ||
                dropdownChains?.[dependencyFieldIdx]?.[0] ||
                "";

            if (!selectedValue) {
              toast({
                title: "Selection Required",
                description: `Please select a value for "${dependencyField.fieldTitle}"`,
                variant: "destructive",
                duration: 3000,
              });
              setLoading(false);
              return;
            }

            // Find the corresponding submit_type configuration
            const submitTypeConfig =
              submitField.submitConfig.submit_types?.find((st: any) => {
                // Compare with type coercion to handle string vs number mismatches
                const matches =
                  String(st.option_given_name).toLowerCase() ===
                  String(selectedValue).toLowerCase();
                return matches;
              });

            if (!submitTypeConfig) {
              toast({
                title: "Configuration Error",
                description: `No configuration found for selected option "${selectedValue}"`,
                variant: "destructive",
                duration: 3000,
              });
              setLoading(false);
              return;
            }

            bundleId = submitTypeConfig.bundleId;
            requestId = submitTypeConfig.requestId || "all_bundle_requests";
          } else {
            // Normal mode - use bundleId and requestId from submitConfig
            bundleId = submitField.submitConfig.bundleId;
            requestId =
              submitField.submitConfig.requestId || "all_bundle_requests";
          }

          if (!bundleId) {
            toast({
              title: "Configuration Error",
              description: "Missing bundleId in submit configuration",
              variant: "destructive",
              duration: 3000,
            });
            setLoading(false);
            return;
          }

          let bundle: any = null;
          if (isMeshV2) {
            const allBundles = logicPageInfo?.data?.LogicBundles || [];
            bundle = allBundles.find((b: any) => b.bundleId == bundleId);
          } else {
            // Mesh v1 compatibility: support saved submit config values mapped to
            // either bundleId or id and backfill missing cached logic bundles.
            let allBundles = logicPageInfo?.data?.LogicBundles || [];
            if ((!allBundles || allBundles.length === 0) && meshPluginId) {
              try {
                const freshLogicPageInfo =
                  await fetchLogicPageInfo(meshPluginId);
                allBundles = freshLogicPageInfo?.data?.LogicBundles || [];
              } catch (error) {
                console.error(
                  "Failed to fetch logic bundles for submit:",
                  error,
                );
              }
            }

            const normalizedBundleId = String(bundleId);
            bundle = allBundles.find((b: any) => {
              const candidates = [
                b?.bundleId,
                b?.id,
                b?.uid,
                b?.workflow_id,
              ].filter((val) => val !== undefined && val !== null);

              return candidates.some(
                (candidate) => String(candidate) === normalizedBundleId,
              );
            });
          }

          if (!bundle) {
            toast({
              title: "Bundle Not Found",
              description: `Bundle with ID ${bundleId} not found`,
              variant: "destructive",
              duration: 3000,
            });
            setLoading(false);
            return;
          }

          // Determine which requests to call
          const requestsToCall =
            requestId && requestId !== "all_bundle_requests"
              ? isMeshV2
                ? (bundle.requests || []).filter(
                    (req: any) => req.requestId == requestId,
                  )
                : (bundle.requests || []).filter(
                    (req: any) =>
                      req?.requestId == requestId ||
                      req?.id == requestId ||
                      String(req?.requestId) === String(requestId) ||
                      String(req?.id) === String(requestId),
                  )
              : bundle.requests;

          if (requestsToCall.length === 0) {
            toast({
              title: "No Requests Found",
              description:
                requestId && requestId !== "all_bundle_requests"
                  ? `Request with ID ${requestId} not found in bundle`
                  : "No requests found in the selected bundle",
              variant: "destructive",
              duration: 3000,
            });
            setLoading(false);
            return;
          }

          // Update definedVariables with current form values
          const updatedVariables = definedVariables.map((variable) => {
            const matchingField = Meshfields.find(
              (field: any) =>
                field.variableName === variable.name ||
                field.givenName === variable.name,
            );

            if (matchingField) {
              const fieldValue = formValues[matchingField.fieldId];
              if (fieldValue !== undefined && fieldValue !== null) {
                return { ...variable, value: fieldValue };
              }
            }

            return variable;
          });

          // Add form values as variables if not already defined
          Object.entries(formValues).forEach(([fieldId, value]) => {
            const field = Meshfields.find((f: any) => f.fieldId === fieldId);
            if (field) {
              // Use variableName or givenName, whichever is available
              const varName = field.variableName || field.givenName;

              if (varName) {
                const existingVar = updatedVariables.find(
                  (v) => v.name === varName,
                );
                if (!existingVar) {
                  updatedVariables.push({
                    name: varName,
                    value: value,
                    type: "single",
                    scope: "local",
                    sourceBundleId: 0,
                    sourceRequestId: 0,
                    sourcePath: "",
                  });
                }
              }
            }
          });

          // Get configuration using the helper function that handles both import_settings and configDropdownTree
          const configResult = getSelectedConfig();
          const configData = configResult?.config || null;
          const tabUid = configResult?.tab_uid || "";

          // Construct data object from form values using the helper function
          const dataFromFormValues = constructDataFromFormValues();

          // Process each request in the list
          const results: {
            success: boolean;
            requestId: number;
            error?: string;
          }[] = [];

          for (const requestDataItem of requestsToCall) {
            try {
              //  Step 1: Interpolate defined variables first
              let interpolatedUrl = interpolateDefinedVariablesOnly(
                requestDataItem.url || "",
                updatedVariables,
              );

              let interpolatedBody = interpolateDefinedVariablesOnly(
                requestDataItem.body || "",
                updatedVariables,
              );
              let interpolatedToken = interpolateDefinedVariablesOnly(
                requestDataItem.auth?.token || "",
                updatedVariables,
              );

              //  Step 2: Then interpolate anomaly variables
              interpolatedUrl = interpolateAnomalyVariables(
                interpolatedUrl,
                anomalyFieldSelections,
              );
              interpolatedBody = interpolateAnomalyVariables(
                interpolatedBody,
                anomalyFieldSelections,
              );
              interpolatedToken = interpolateAnomalyVariables(
                interpolatedToken,
                anomalyFieldSelections,
              );

              //  Step 3: Interpolate headers with both defined and anomaly variables
              const interpolatedHeaders = (requestDataItem.headers || []).map(
                (h: any) => {
                  let key = interpolateDefinedVariablesOnly(
                    h.key,
                    updatedVariables,
                  );
                  let value = interpolateDefinedVariablesOnly(
                    h.value,
                    updatedVariables,
                  );

                  // Then interpolate anomaly variables
                  key = interpolateAnomalyVariables(
                    key,
                    anomalyFieldSelections,
                  );
                  value = interpolateAnomalyVariables(
                    value,
                    anomalyFieldSelections,
                  );

                  return { key, value };
                },
              );

              // Prepare config and data objects in the correct format
              // Config should only contain metadata
              const configPayload = configData
                ? {
                    tabId: tabUid,
                    uid: configData.uid,
                    configType: "original",
                  }
                : null;

              // Data should contain all config fields (excluding metadata) + action fields
              const dataPayload: any = { ...dataFromFormValues };

              // Add config fields to data (excluding uid, isDecript, and other metadata)
              if (configData) {
                Object.entries(configData).forEach(([key, value]) => {
                  // Exclude metadata fields
                  if (
                    key !== "uid" &&
                    key !== "isDecript" &&
                    key !== "tabId" &&
                    key !== "configType"
                  ) {
                    dataPayload[key] = value;
                  }
                });
              }

              // Build the payload with interpolated values
              const payload = {
                ...requestDataItem,
                url: interpolatedUrl,
                headers: interpolatedHeaders,
                body: interpolatedBody || null,
                auth: {
                  ...requestDataItem.auth,
                  token: interpolatedToken,
                },
                data: dataPayload,
                config: configPayload,
                flag: 1,
                ssl_verify: requestDataItem.ssl_verify ?? true,
                plugin_id: meshPluginId || "",
                configuration_Id: configData?.uid || Date.now().toString(),
                // Add metadata including anomaly selections
                metadata: {
                  anomalyId: anmlId,
                  anomalyType: anomalyType,
                  anomalyTime: anomalyTime,
                  formValues: formValues,
                  dropdownChains: dropdownChains,
                  entityData: entityData,
                  anomalySelections: anomalyFieldSelections,
                  timestamp: Date.now(),
                },
              };

              // Make the API call
              const response = await sendRequest(payload);
              results.push({
                success: true,
                requestId: requestDataItem.requestId,
              });

              // Update variables with response data for next request
              // Find all variables that source from this request
              const variablesToUpdate = updatedVariables.filter(
                (v) =>
                  (isMeshV2
                    ? v.sourceBundleId == Number(bundleId)
                    : String(v.sourceBundleId ?? "") === String(bundleId) ||
                      String(v.sourceBundleId) ===
                        String(bundle?.bundleId ?? "") ||
                      String(v.sourceBundleId) === String(bundle?.id ?? "")) &&
                  v.sourceRequestId == requestDataItem.requestId,
              );

              // Update each variable with the response data
              variablesToUpdate.forEach((variable) => {
                const resValue =
                  response?.body?.data ?? response?.body ?? response;

                // Extract value using sourcePath
                const path = variable.sourcePath || "";

                // Handle array notation in path like [0].userId
                const normalizedPath = path.replace(/\[(\d+)\]/g, ".$1");
                const pathParts = normalizedPath.split(".").filter(Boolean);

                let currentValue = resValue;

                for (const part of pathParts) {
                  if (currentValue && typeof currentValue === "object") {
                    // Check if part is numeric (array index)
                    const index = Number(part);
                    if (!isNaN(index) && Array.isArray(currentValue)) {
                      currentValue = currentValue[index];
                    } else if (part in currentValue) {
                      currentValue = currentValue[part];
                    } else if (part === "root") {
                      currentValue = resValue;
                    } else {
                      currentValue = undefined;
                      console.error(
                        ` Path part "${part}" not found in response. Available keys:`,
                        Object.keys(currentValue),
                      );
                      break;
                    }
                  } else {
                    console.error(
                      ` Cannot navigate further, current value is not an object:`,
                      currentValue,
                    );
                    currentValue = undefined;
                    break;
                  }
                }

                // Update the variable in updatedVariables array
                const varIndex = updatedVariables.findIndex(
                  (v) => v.name === variable.name,
                );
                if (varIndex !== -1) {
                  updatedVariables[varIndex] = {
                    ...updatedVariables[varIndex],
                    value: currentValue,
                  };
                } else {
                  console.error(
                    ` Variable "${variable.name}" not found in updatedVariables array`,
                  );
                }
              });
            } catch (error: any) {
              console.error(
                `Error executing request ${requestDataItem.requestId}:`,
                error,
              );
              results.push({
                success: false,
                requestId: requestDataItem.requestId,
                error: error.message || "Request failed",
              });
            }
          }

          // Show results
          const successCount = results.filter((r) => r.success).length;
          const failCount = results.length - successCount;

          if (failCount === 0) {
            toast({
              title: "Success!",
              description: "Action has been submitted successfully",
              variant: "success",
              duration: 3000,
            });
            // Only close the dialog on success
            setTimeout(() => {
              onClose();
            }, 1500);
          } else if (successCount === 0) {
            toast({
              title: "Failed",
              description: "The action submission has been failed to execute",
              variant: "destructive",
              duration: 4000,
            });
            // Don't close on failure - let user retry or check the form
          }
        } else {
          // Handle non-mesh plugin submission (legacy)

          const form = document.getElementById(
            "pluginActionViewForm",
          ) as HTMLFormElement;
          if (form) {
            form.submit();
          }

          toast({
            title: "Action Submitted",
            description: "The action has been submitted",
            variant: "default",
            duration: 3000,
          });
        }
      } catch (error: any) {
        console.error(" Submit Error:", error);

        // Extract error message
        let errorMessage = "Failed to submit action";
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        showToast({
          title: "Submission Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    };

    // Helper function to construct data object from form values
    const constructDataFromFormValues = (
      currentFieldId?: string,
      currentValue?: any,
    ): any => {
      const dataObj: any = {};

      Meshfields.forEach((field: any, fieldIndex: number) => {
        // Exclude only import_settings and isConfig fields
        if (field.fieldType !== "import_settings" && !field.isConfig) {
          // Use current value if this is the field being updated
          const fieldValue =
            currentFieldId === field.fieldId
              ? currentValue
              : formValues[field.fieldId];

          // Use variableName or givenName as the key for data
          // For anomaly fields, use the anomaly key (like "ip", "mac") instead of givenName
          let dataKey: string;
          if (field.isAnomaly && field.values?.[0]?.value) {
            // Use the anomaly key (e.g., "ip", "mac") from the values array
            dataKey = `${field.values[0].value}__action`;
          } else {
            // Use variableName or givenName as the key for data, with __action suffix
            // Replace spaces with underscores and convert to lowercase
            const baseKey = (field.variableName || field.givenName)
              .replace(/\s+/g, "_")
              .toLowerCase();
            dataKey = `${baseKey}__action`;
          }

          if (
            fieldValue !== undefined &&
            fieldValue !== null &&
            fieldValue !== ""
          ) {
            dataObj[dataKey] = fieldValue;
          }
        }

        // Include dropdown chain selections (nested target fields) with actual field names
        const chains = dropdownChains?.[fieldIndex];
        if (chains) {
          Object.entries(chains).forEach(([key, value]) => {
            // Skip the main dropdown selection (level 0)
            if (key === "0") return;

            // Handle target field selections (target-level-index format)
            if (key.startsWith("target-") && value) {
              // Parse the key to get level and index
              const [, levelStr, idxStr] = key.split("-");
              const level = parseInt(levelStr);
              const targetIdx = parseInt(idxStr);

              // Find the actual target field name from the field structure
              let targetFieldName = null;

              // Navigate through the tree to find the target field
              const tree = field.configDropdownTree || field.dropdownTree || [];

              // Helper function to find targetField by navigating through nested structure
              const findTargetFieldByPath = (
                nodes: any[],
                targetLevel: number,
                targetIndex: number,
              ): any => {
                // For level 1, look directly in root nodes' targetFields
                if (targetLevel === 1) {
                  for (const node of nodes) {
                    if (node.targetFields && node.targetFields[targetIndex]) {
                      return node.targetFields[targetIndex];
                    }
                  }
                  return null;
                }

                // For level 2+, we need to find the path through parent selections
                // First, find level 1 targetField
                const level1Selection =
                  chains[`target-1-${targetIdx}`] || chains[`target-1-0`];

                if (!level1Selection) {
                  // Try to find in any node's targetFields recursively
                  for (const node of nodes) {
                    if (node.targetFields) {
                      for (let i = 0; i < node.targetFields.length; i++) {
                        const tf = node.targetFields[i];

                        // If this is the level we're looking for
                        if (targetLevel === 2 && i === targetIndex) {
                          return tf;
                        }

                        // Look in nested targetFields
                        if (tf.targetFields && tf.targetFields.length > 0) {
                          const found = findInNestedTargetFields(
                            tf.targetFields,
                            targetLevel - 1,
                            targetIndex,
                          );
                          if (found) return found;
                        }

                        // Look in previewOptions' targetFields
                        if (
                          tf.previewOptions &&
                          Array.isArray(tf.previewOptions)
                        ) {
                          for (const preview of tf.previewOptions) {
                            if (
                              preview.targetFields &&
                              preview.targetFields.length > 0
                            ) {
                              const found = findInNestedTargetFields(
                                preview.targetFields,
                                targetLevel - 1,
                                targetIndex,
                              );
                              if (found) return found;
                            }
                          }
                        }
                      }
                    }
                  }
                  return null;
                }

                // If we have level 1 selection, navigate through that path
                for (const node of nodes) {
                  if (node.targetFields) {
                    for (const level1Field of node.targetFields) {
                      // Check if level1Field has nested targetFields
                      if (
                        level1Field.targetFields &&
                        level1Field.targetFields[targetIndex]
                      ) {
                        return level1Field.targetFields[targetIndex];
                      }

                      // Check previewOptions
                      if (level1Field.previewOptions) {
                        for (const preview of level1Field.previewOptions) {
                          if (
                            preview.targetFields &&
                            preview.targetFields[targetIndex]
                          ) {
                            return preview.targetFields[targetIndex];
                          }
                        }
                      }
                    }
                  }
                }

                return null;
              };

              // Helper to search in nested targetFields
              const findInNestedTargetFields = (
                targetFields: any[],
                remainingLevels: number,
                index: number,
              ): any => {
                if (remainingLevels === 1) {
                  return targetFields[index] || null;
                }

                for (const tf of targetFields) {
                  if (tf.targetFields && tf.targetFields.length > 0) {
                    const found = findInNestedTargetFields(
                      tf.targetFields,
                      remainingLevels - 1,
                      index,
                    );
                    if (found) return found;
                  }
                }

                return null;
              };

              const targetField = findTargetFieldByPath(tree, level, targetIdx);
              targetFieldName =
                targetField?.fieldName || targetField?.variableName || key;

              // Replace spaces with underscores and convert to lowercase
              targetFieldName = targetFieldName
                .replace(/\s+/g, "_")
                .toLowerCase();

              // Use the actual field name with __action suffix
              dataObj[`${targetFieldName}__action`] = value;
            }
            // Handle children dropdown selections (old structure)
            else if (!key.startsWith("target-") && value) {
              // Use field name with level indicator, convert to lowercase
              const baseKey = (field.variableName || field.givenName)
                .replace(/\s+/g, "_")
                .toLowerCase();
              const levelKey = `${baseKey}_level_${key}__action`;
              dataObj[levelKey] = value;
            }
          });
        }
      });

      return dataObj;
    };

    // Helper function to get selected config
    const getSelectedConfig = (
      currentConfigValue?: string,
      currentFieldIdx?: number,
    ): { config: any; tab_uid: string } | null => {
      // Check for import_settings field
      const settingsField = Meshfields.find(
        (field: any) => field.fieldType === "import_settings",
      );

      if (settingsField) {
        const settingsValue = formValues[settingsField.fieldId];
        if (
          settingsValue &&
          settingsValue !== "" &&
          settingsValue !== "none" &&
          settingsValue !== "select"
        ) {
          // Find the config from meshPluginsDropdown
          const configData = meshPluginsDropdown.data?.config_data || {};

          // Search through all tab_uids to find the matching config
          for (const [tab_uid, configArray] of Object.entries(configData)) {
            if (Array.isArray(configArray)) {
              const selectedConfig = configArray.find(
                (c: any) => c.uid === settingsValue,
              );
              if (selectedConfig) {
                return { config: selectedConfig, tab_uid };
              }
            }
          }
        }
      }

      // Check for configDropdownTree field
      const configField = Meshfields.find(
        (field: any) =>
          field.configDropdownTree &&
          Array.isArray(field.configDropdownTree) &&
          field.configDropdownTree.length > 0,
      );

      if (configField) {
        const configFieldIdx = Meshfields.findIndex(
          (field: any) => field.fieldId === configField.fieldId,
        );

        // Use current value if this is the config field being updated
        const configValue =
          currentFieldIdx === configFieldIdx && currentConfigValue
            ? currentConfigValue
            : dropdownChains?.[configFieldIdx]?.[0];

        if (configValue && configValue !== "" && configValue !== "select") {
          // Find the config from meshPluginsDropdown
          const configData = meshPluginsDropdown.data?.config_data || {};

          // Search through all tab_uids to find the matching config
          for (const [tab_uid, configArray] of Object.entries(configData)) {
            if (Array.isArray(configArray)) {
              const selectedConfig = configArray.find(
                (c: any) => c.uid === configValue,
              );
              if (selectedConfig) {
                return { config: selectedConfig, tab_uid };
              }
            }
          }
        }
      }

      return null;
    };

    // Update the refreshVariableData function to also check configDropdownTree
    const refreshVariableData = async (
      variable: DefinedVariable,
      fieldIndex: number,
      currentFieldId?: string,
      currentFieldValue?: any,
      // parentNode?: any,
    ): Promise<void> => {
      if (!variable?.name) return;

      // Prevent recursive loops in dependency chains
      if (inFlightVariableRefresh.current.has(variable.name)) {
        return;
      }
      inFlightVariableRefresh.current.add(variable.name);

      try {
        const bundleId = variable?.sourceBundleId;
        const requestId = variable?.sourceRequestId;

        const allBundles = logicPageInfo?.data?.LogicBundles || [];

        // Find the request data from logicPageInfo with requestId and bundleId

        const bundle = allBundles?.find(
          (b: any) =>
            b?.id == bundleId ||
            b?.bundleId == bundleId ||
            String(b?.id) === String(bundleId) ||
            String(b?.bundleId) === String(bundleId),
        );

        const requestDataItem = bundle?.requests?.find(
          (req: any) =>
            req?.requestId == requestId ||
            req?.id == requestId ||
            String(req?.requestId) === String(requestId) ||
            String(req?.id) === String(requestId),
        );

        if (!requestDataItem) {
          console.warn(` Request not found for variable "${variable.name}"`);
          return;
        }

        // Resolve dependent variables used by this request before executing it.
        // This ensures placeholders like {{token}} are prefetched when needed.
        const dependentVarNames = collectRequestTemplateVariables(
          requestDataItem,
        ).filter((name) => name !== variable.name);

        for (const depVarName of dependentVarNames) {
          if (inFlightVariableRefresh.current.has(depVarName)) continue;

          const depVar = getCurrentDefinedVariables().find(
            (v) => v.name === depVarName,
          );
          if (!depVar) continue;
          if (hasResolvedValue(depVar.value)) continue;

          await refreshVariableData(
            depVar,
            fieldIndex,
            currentFieldId,
            currentFieldValue,
          );
        }

        const liveDefinedVariables = getCurrentDefinedVariables();

        // Interpolate variables in the request data
        const interpolatedHeaders = (requestDataItem.headers || []).map(
          (h: any) => ({
            key: interpolateDefinedVariablesOnly(h.key, liveDefinedVariables),
            value: interpolateDefinedVariablesOnly(
              h.value,
              liveDefinedVariables,
            ),
          }),
        );

        const interpolatedBody = interpolateDefinedVariablesOnly(
          requestDataItem.body || "",
          liveDefinedVariables,
        );

        const interpolatedToken = interpolateDefinedVariablesOnly(
          requestDataItem.auth?.token || "",
          liveDefinedVariables,
        );

        const interpolatedUrl = interpolateDefinedVariablesOnly(
          requestDataItem.url || "",
          liveDefinedVariables,
        );

        // Construct data from current form values (including the value being selected)
        const formData = constructDataFromFormValues(
          currentFieldId,
          currentFieldValue,
        );

        // Get selected config (including if this is a config dropdown being selected)
        const selectedConfigResult = getSelectedConfig(
          currentFieldValue,
          fieldIndex,
        );

        // Structure data and config like FormWrapper does
        const EXCLUDE_DATA_KEYS = ["uid", "isDecript"];

        // Config field values go into data (without uid, isDecript)
        let configData = {};
        // Config metadata goes into config
        let configInfo: any = null;

        if (selectedConfigResult) {
          const { config: selectedConfig, tab_uid } = selectedConfigResult;

          configInfo = {
            uid: selectedConfig.uid,
            configType: "original",
            tabId: tab_uid || "",
          };

          // Extract config fields into data
          configData = Object.fromEntries(
            Object.entries(selectedConfig).filter(
              ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
            ),
          );
        }

        // Merge form data with config data
        const finalData = {
          ...configData, // Config fields (ip_address, port, api_key, etc.)
          ...formData, // Action form field values
        };

        const payload = {
          ...requestDataItem,
          url: interpolatedUrl,
          headers: interpolatedHeaders,
          body: interpolatedBody || null,
          auth: {
            ...requestDataItem.auth,
            token: interpolatedToken,
          },
          data: finalData, // Combined form + config field values
          config: configInfo, // Config metadata { uid, configType, tab_uid }
          flag: 1,
          ssl_verify: requestDataItem.ssl_verify ?? true,
          plugin_id: meshPluginId || "",
          configuration_Id: selectedConfigResult?.config?.uid || "",
        };

        // Make the API call
        const response = await sendRequest(payload);
        const resValue = response?.body?.data ?? response;

        // Extract value using sourcePath
        const path = variable?.sourcePath || "";
        const pathParts = path.split(".");
        let currentValue = resValue;

        for (const part of pathParts) {
          if (currentValue && part in currentValue) {
            currentValue = currentValue[part];
          } else if (part === "root") {
            currentValue = resValue;
          } else {
            currentValue = undefined;
            console.error(` Path part "${part}" not found in response.`);
            break;
          }
        }

        // Update the variable in definedVariables
        setDefinedVariables?.((prev) => {
          const base = prev.length ? prev : getCurrentDefinedVariables();
          const exists = base.some((v) => v.name === variable.name);
          const next = exists
            ? base.map((v) =>
                v.name === variable.name ? { ...v, value: currentValue } : v,
              )
            : [...base, { ...variable, value: currentValue }];
          definedVariablesRef.current = next;
          return next;
        });

        //  Update BOTH dropdownTree AND configDropdownTree
        const fieldData = Meshfields[fieldIndex];

        // Function to recursively update targetFields
        const updateTargetFieldsRecursively = (nodes: any[]) => {
          nodes.forEach((node: any) => {
            if (node.targetFields) {
              node.targetFields.forEach((targetField: any) => {
                if (targetField.variableName === variable.name) {
                  const labelKey = targetField.labelKey || "";
                  const valueKey = targetField.valueKey || "";

                  let newPreviewOptions: any[] = [];

                  if (Array.isArray(currentValue)) {
                    newPreviewOptions = currentValue.map((item: any) => {
                      const label =
                        labelKey && item[labelKey]
                          ? item[labelKey]
                          : item.label ||
                            item.name ||
                            item.title ||
                            String(item);

                      const value =
                        valueKey && item[valueKey]
                          ? item[valueKey]
                          : item.value || item.id || item.uid || String(item);

                      const existingTargetFields =
                        targetField.previewOptions?.[0]?.targetFields || [];

                      return {
                        label: String(label),
                        value: String(value),
                        targetFields: existingTargetFields,
                        children: [],
                        _rawData: item,
                      };
                    });
                  } else if (currentValue) {
                    const existingTargetFields =
                      targetField.previewOptions?.[0]?.targetFields || [];

                    newPreviewOptions = [
                      {
                        label: String(currentValue),
                        value: String(currentValue),
                        targetFields: existingTargetFields,
                        children: [],
                        _rawData: currentValue,
                      },
                    ];
                  }

                  targetField.previewOptions = newPreviewOptions;
                }

                // Recursively check nested targetFields
                if (
                  targetField.previewOptions &&
                  Array.isArray(targetField.previewOptions)
                ) {
                  targetField.previewOptions.forEach((previewOpt: any) => {
                    if (
                      previewOpt.targetFields &&
                      Array.isArray(previewOpt.targetFields)
                    ) {
                      updateTargetFieldsRecursively([
                        { targetFields: previewOpt.targetFields },
                      ]);
                    }
                  });
                }
              });
            }

            if (node.children && Array.isArray(node.children)) {
              updateTargetFieldsRecursively(node.children);
            }
          });
        };

        //  Update dropdownTree if exists
        if (fieldData?.dropdownTree) {
          updateTargetFieldsRecursively(fieldData.dropdownTree);
        }

        //  Update configDropdownTree if exists
        if (fieldData?.configDropdownTree) {
          updateTargetFieldsRecursively(fieldData.configDropdownTree);
        }

        // Store the data in runtimeDropdownData
        const runtimeKey = `${fieldIndex}-1-${variable.name}`;

        if (Array.isArray(currentValue)) {
          setRuntimeDropdownData((prev) => ({
            ...prev,
            [runtimeKey]: currentValue,
          }));
        } else if (currentValue) {
          setRuntimeDropdownData((prev) => ({
            ...prev,
            [runtimeKey]: [currentValue],
          }));
        }
      } catch (error) {
        console.error(` Error refreshing variable "${variable.name}":`, error);
        throw error;
      } finally {
        inFlightVariableRefresh.current.delete(variable.name);
      }
    };

    // Add this function after refreshVariableData
    const extractAllNestedVariables = (targetFields: any[]): string[] => {
      const variables: string[] = [];

      const traverse = (fields: any[]) => {
        fields.forEach((field: any) => {
          // Add variable if it has bundleId and variableName (needs API call)
          if (
            field.variableName &&
            field.bundleId &&
            field.sourceType === "bundle"
          ) {
            if (!variables.includes(field.variableName)) {
              variables.push(field.variableName);
            }
          }

          // Check nested targetFields in previewOptions
          if (field.previewOptions && Array.isArray(field.previewOptions)) {
            field.previewOptions.forEach((previewOpt: any) => {
              if (
                previewOpt.targetFields &&
                Array.isArray(previewOpt.targetFields)
              ) {
                traverse(previewOpt.targetFields);
              }
            });
          }

          // Check nested targetFields directly
          if (field.targetFields && Array.isArray(field.targetFields)) {
            traverse(field.targetFields);
          }
        });
      };

      traverse(targetFields);
      return variables;
    };

    // Add this function after extractAllNestedVariables
    const checkSettingsFieldFilled = (): boolean => {
      // Check 1: Look for import_settings field
      const settingsField = Meshfields.find(
        (field: any) => field.fieldType === "import_settings",
      );

      if (settingsField) {
        const settingsValue = formValues[settingsField.fieldId];

        if (
          settingsValue &&
          settingsValue !== "" &&
          settingsValue !== "none" &&
          settingsValue !== "select"
        ) {
          return true; // import_settings is filled
        }
      }

      // Check 2: Look for configDropdownTree field
      const configField = Meshfields.find(
        (field: any) =>
          field.configDropdownTree &&
          Array.isArray(field.configDropdownTree) &&
          field.configDropdownTree.length > 0,
      );

      if (configField) {
        const configFieldIdx = Meshfields.findIndex(
          (field: any) => field.fieldId === configField.fieldId,
        );

        const configValue = dropdownChains?.[configFieldIdx]?.[0];

        if (configValue && configValue !== "" && configValue !== "select") {
          return true; // configDropdownTree is selected
        }
      }

      // Check 3: If neither exists, allow API calls
      if (!settingsField && !configField) {
        return true;
      }

      // If either exists but neither is filled, return false
      return false;
    };

    // API dropdown handler for Regualar dropdowns
    const handleApiOptionChange = async (
      val: string,
      fieldIdx: number,
      fieldId: string,
    ) => {
      if (val === "select" || val === "") {
        setDropdownChains?.((prev) => {
          const updated = { ...prev };
          delete updated[fieldIdx];
          return updated;
        });
        handleFieldChange(fieldId, "");
        return;
      }

      //  Check if settings field is filled before proceeding
      if (!checkSettingsFieldFilled()) {
        // Determine which one is missing for better error message
        const settingsField = Meshfields.find(
          (field: any) => field.fieldType === "import_settings",
        );
        const configField = Meshfields.find(
          (field: any) =>
            field.configDropdownTree &&
            Array.isArray(field.configDropdownTree) &&
            field.configDropdownTree.length > 0,
        );

        let errorMessage = "Please select a configuration before proceeding.";

        if (settingsField && configField) {
          errorMessage =
            "Please select either a configuration from Settings or Config dropdown before proceeding.";
        } else if (settingsField) {
          errorMessage =
            "Please select a configuration from the Settings field before proceeding.";
        } else if (configField) {
          errorMessage =
            "Please select a configuration from the Config dropdown before proceeding.";
        }

        showToast({
          title: "Configuration Required",
          description: errorMessage,
          variant: "destructive",
          duration: 3000,
        });
        return;
      }

      // Find the target field according to the value
      const allData = Meshfields[fieldIdx];
      const tree = allData.dropdownTree || [];

      // Find the selected node
      const selectedNode = tree.find((node: any) => node.value === val);

      if (!selectedNode) {
        console.warn(" Selected node not found");
        return;
      }

      //  Extract ALL variables including nested ones
      const targetFields = selectedNode.targetFields || [];
      const allVariables = extractAllNestedVariables(targetFields);

      // Update dropdown chains
      setDropdownChains?.((prev) => ({
        ...prev,
        [fieldIdx]: { 0: val },
      }));
      handleFieldChange(fieldId, val);

      // Process variables sequentially
      if (allVariables.length > 0) {
        setLoading(true);

        try {
          // Process each variable one by one
          for (const varName of allVariables) {
            const variable = getCurrentDefinedVariables().find(
              (v) => v.name === varName,
            );

            if (!variable) {
              console.warn(
                ` Variable "${varName}" not found in definedVariables`,
              );
              continue;
            }

            // Call the refresh function and wait for it to complete, passing the current field value
            await refreshVariableData(variable, fieldIdx, fieldId, val);
          }

          //  Force re-render by updating Meshfields state
          setFormValues((prev) => ({ ...prev, _refresh: Date.now() }));
        } catch (error: any) {
          console.error(" Error refreshing variables:", error);
          toast({
            title: "Error",
            description: `Failed to refresh variables: ${error.message}`,
            variant: "destructive",
            duration: 3000,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    // Helper to fetch targetField options dynamically
    const fetchConfigTargetFieldOptions = async (
      configValue: string,
      selectedValue: string,
      targetField: any,
      fieldIdx: number,
      parentSelections: Record<string, string> = {},
    ): Promise<void> => {
      const cacheKey = `${configValue}-${targetField.fieldName}-${selectedValue}`;

      // Check if already fetched
      if (
        configTargetFieldData[configValue]?.[targetField.fieldName]?.[
          selectedValue
        ]
      ) {
        return;
      }

      // Check if request is already in-flight
      if (inFlightRequests.current.has(cacheKey)) {
        return;
      }

      // Check if sourceType is "default" - these don't need API calls
      if (targetField.sourceType === "default") {
        const options = targetField.defaultOptions || [];
        setConfigTargetFieldData((prev) => ({
          ...prev,
          [configValue]: {
            ...prev[configValue],
            [targetField.fieldName]: {
              ...prev[configValue]?.[targetField.fieldName],
              [selectedValue]: options,
            },
          },
        }));
        return;
      }

      if (!targetField.bundleId || !targetField.variableName) {
        console.warn(
          " Missing bundleId or variableName for targetField:",
          targetField,
          "- No options will be shown (API call required)",
        );
        return;
      }

      // Mark request as in-flight
      inFlightRequests.current.add(cacheKey);
      setLoadingDropdowns((prev) => ({ ...prev, [cacheKey]: true }));

      // Increment active API calls counter and show loader
      activeApiCallsCount.current += 1;
      setLoading(true);

      try {
        // Check if logicPageInfo is available
        if (!logicPageInfo?.data?.LogicBundles) {
          console.error(" logicPageInfo not loaded yet:", {
            logicPageInfo,
            meshPluginId,
          });
          throw new Error(
            "Logic page data not loaded. Please wait and try again.",
          );
        }

        // Find the bundle - match by bundleId only (ignore logicType since it might be undefined)
        const authBundle = logicPageInfo.data.LogicBundles.find(
          (bundle: any) => {
            const match =
              String(bundle.bundleId) === String(targetField.bundleId);

            return match;
          },
        );

        // if (authBundle) {
        //   console.log(` Found bundle:`, {
        //     bundleId: authBundle.bundleId,
        //     name: authBundle.name || authBundle.bundleName || 'Unnamed',
        //     logicType: authBundle.logicType,
        //     bundleType: authBundle.bundleType,
        //     requestsCount: authBundle.requests?.length || 0,
        //   });
        // }

        if (!authBundle) {
          const availableBundlesList = logicPageInfo.data.LogicBundles.map(
            (b: any) => ({
              name: b.name,
              id: b.bundleId,
              type: b.logicType,
              requests: b.requests?.map((r: any) => r.name) || [],
            }),
          );

          console.warn(
            availableBundlesList,
            ` Bundle with ID "${targetField.bundleId}" not found. Check if bundleId is correct and matches any of the available bundles above.`,
          );

          throw new Error(
            `Bundle ID "${targetField.bundleId}" not found. Check console for available bundles.`,
          );
        }

        // Find the variable definition from definedVariables array
        const variableDefinition = definedVariables?.find((v: any) => {
          const bundleMatches =
            String(v.sourceBundleId) === String(targetField.bundleId);
          const nameMatches = v.name === targetField.variableName;
          return bundleMatches && nameMatches;
        });

        if (!variableDefinition) {
          console.error(
            ` Variable "${targetField.variableName}" not found in definedVariables for bundle ${targetField.bundleId}`,
            "\n Available variables:",
            definedVariables
              ?.filter(
                (v: any) =>
                  String(v.sourceBundleId) === String(targetField.bundleId),
              )
              .map((v: any) => v.name),
          );
          throw new Error(
            `Variable "${targetField.variableName}" not found in definitions for bundle ${targetField.bundleId}.`,
          );
        }

        // Use the sourceRequestId from the variable definition to find the request
        const requestId = variableDefinition.sourceRequestId;
        const variableRequest = authBundle.requests?.find(
          (req: any) =>
            req.requestId == requestId ||
            req.id == requestId ||
            String(req.requestId) === String(requestId) ||
            String(req.id) === String(requestId),
        );

        if (!variableRequest) {
          console.error(
            ` Request with ID "${requestId}" not found in bundle ${targetField.bundleId}`,
            "\n Available requests:",
            authBundle.requests?.map((r: any) => ({
              id: r.id,
              requestId: r.requestId,
              url: r.url,
            })),
          );
          throw new Error(
            `Request ID "${requestId}" not found in bundle ${targetField.bundleId}.`,
          );
        }

        // Get the parent field's fieldId to construct proper data object
        const parentField = Meshfields[fieldIdx];
        const parentFieldId = parentField?.fieldId;

        // Build data and config objects like FormWrapper does
        const selectedConfigResult = getSelectedConfig(configValue, fieldIdx);

        const EXCLUDE_DATA_KEYS = ["uid", "isDecript"];

        // Config field values go into data (without uid, isDecript)
        let configData = {};
        let configInfo: any = null;

        if (selectedConfigResult) {
          const selectedConfig = selectedConfigResult.config;

          configInfo = {
            tabId: selectedConfigResult.tab_uid,
            uid: selectedConfig.uid,
            configType: "original",
          };

          configData = Object.fromEntries(
            Object.entries(selectedConfig).filter(
              ([key]) => !EXCLUDE_DATA_KEYS.includes(key),
            ),
          );
        }

        // Build form data object
        const formDataObj = constructDataFromFormValues(
          parentFieldId,
          configValue,
        );

        // Build action data object (only form values + parent selections, NOT config fields)
        const actionDataObj = {
          ...formDataObj, // Action form values
        };

        // Add parent selections to action data object (for nested API calls like vdoms="root")
        Object.entries(parentSelections).forEach(([key, value]) => {
          if (value) {
            actionDataObj[key] = value;
          }
        });

        // Transform action data keys by appending __action suffix
        const transformedActionData: Record<string, any> = {};
        Object.entries(actionDataObj).forEach(([key, value]) => {
          transformedActionData[`${key}__action`] = value;
        });

        // Merge config data (without suffix) and action data (with __action suffix)
        const finalDataPayload = {
          ...configData, // Config fields without suffix
          ...transformedActionData, // Action fields with __action suffix
        };

        // Build complete data object for interpolation (config + action fields without suffix)
        const dataObjForInterpolation = {
          ...configData, // Config fields for interpolation
          ...formDataObj, // Action fields for interpolation
        };

        // Add parent selections for interpolation
        Object.entries(parentSelections).forEach(([key, value]) => {
          if (value) {
            dataObjForInterpolation[key] = value;
          }
        });

        // Resolve missing variables used by this API request before calling it.
        const prefetchVarNames =
          collectRequestTemplateVariables(variableRequest);
        if (prefetchVarNames.length > 0) {
          for (const varName of prefetchVarNames) {
            if (inFlightVariableRefresh.current.has(varName)) continue;

            const depVar = getCurrentDefinedVariables().find(
              (v) => v.name === varName,
            );
            if (!depVar) continue;
            if (hasResolvedValue(depVar.value)) continue;

            await refreshVariableData(
              depVar,
              fieldIdx,
              parentFieldId,
              configValue,
            );
          }
        }

        const liveDefinedVariables = getCurrentDefinedVariables();

        // Interpolate the request endpoint after dependency prefetch so first call has resolved vars
        let endpoint = variableRequest.endpoint || "";
        endpoint = interpolateAnomalyVariables(
          endpoint,
          anomalyFieldSelections,
        );
        endpoint = interpolateDefinedVariablesOnly(
          endpoint,
          liveDefinedVariables,
        );

        // Replace {{data.xxx}} with actual selected values
        endpoint = endpoint.replace(
          /{{\s*data\.(\w+)\s*}}/g,
          (match: string, key: string) => {
            return dataObjForInterpolation[key] !== undefined
              ? String(dataObjForInterpolation[key])
              : match;
          },
        );

        // Also interpolate body if present
        let body = variableRequest.body || {};
        if (typeof body === "object") {
          body = JSON.parse(
            JSON.stringify(body).replace(
              /{{\s*data\.(\w+)\s*}}/g,
              (match: string, key: string) => {
                return dataObjForInterpolation[key] !== undefined
                  ? String(dataObjForInterpolation[key])
                  : match;
              },
            ),
          );
        }

        // Make API call with data and config like FormWrapper
        const resolvedHeaders = Array.isArray(variableRequest.headers)
          ? variableRequest.headers.map((h: any) => ({
              key: interpolateDefinedVariablesOnly(
                interpolateAnomalyVariables(
                  String(h?.key || ""),
                  anomalyFieldSelections,
                ),
                liveDefinedVariables,
              ),
              value: interpolateDefinedVariablesOnly(
                interpolateAnomalyVariables(
                  String(h?.value || ""),
                  anomalyFieldSelections,
                ),
                liveDefinedVariables,
              ),
            }))
          : variableRequest.headers || {};

        const resolvedAuth =
          variableRequest.auth && typeof variableRequest.auth === "object"
            ? {
                ...variableRequest.auth,
                token: interpolateDefinedVariablesOnly(
                  interpolateAnomalyVariables(
                    String(variableRequest.auth?.token || ""),
                    anomalyFieldSelections,
                  ),
                  liveDefinedVariables,
                ),
                username: interpolateDefinedVariablesOnly(
                  interpolateAnomalyVariables(
                    String(variableRequest.auth?.username || ""),
                    anomalyFieldSelections,
                  ),
                  liveDefinedVariables,
                ),
                password: interpolateDefinedVariablesOnly(
                  interpolateAnomalyVariables(
                    String(variableRequest.auth?.password || ""),
                    anomalyFieldSelections,
                  ),
                  liveDefinedVariables,
                ),
              }
            : variableRequest.auth;

        const payload = {
          ...variableRequest,
          method: variableRequest.method || "GET",
          url: variableRequest.url || "",
          headers: resolvedHeaders,
          body,
          endpoint,
          auth: resolvedAuth,
          data: finalDataPayload, // Config fields (no suffix) + Action fields (with __action suffix)
          config: configInfo, // Include config object
          flag: 1,
          ssl_verify: variableRequest.ssl_verify ?? true,
          plugin_id: meshPluginId || "",
          configuration_Id: configInfo?.uid || "",
        };

        const response: any = await sendRequest(payload);

        const resValue = response?.body?.data ?? response?.data ?? response;

        // Navigate to sourcePath if specified (from variable definition)
        let finalValue = resValue;
        const sourcePath = variableDefinition.sourcePath || "";

        if (sourcePath && sourcePath !== "root") {
          const pathParts = sourcePath.split(".");
          for (const part of pathParts) {
            if (finalValue && typeof finalValue === "object") {
              if (part.includes("[") && part.includes("]")) {
                const arrayMatch = part.match(/^(.+)\[(\d+)\]$/);
                if (arrayMatch) {
                  const [, arrayName, indexStr] = arrayMatch;
                  finalValue = finalValue[arrayName]?.[parseInt(indexStr, 10)];
                }
              } else {
                finalValue = finalValue[part];
              }
            }
          }
        } else if (!sourcePath || sourcePath === "root" || sourcePath === "") {
          // Auto-detect common response patterns if no sourcePath specified
          if (resValue?.data?.results && Array.isArray(resValue.data.results)) {
            finalValue = resValue.data.results;
          } else if (resValue?.results && Array.isArray(resValue.results)) {
            finalValue = resValue.results;
          } else if (resValue?.data && Array.isArray(resValue.data)) {
            finalValue = resValue.data;
          }
        }

        // Generate dropdown options: Use ALL API response data, apply targetFields template
        if (Array.isArray(finalValue) && finalValue.length > 0) {
          // Get targetFields template from first previewOption - all items share same nested structure
          const targetFieldsTemplate =
            targetField.previewOptions?.[0]?.targetFields || [];

          const options = finalValue.map((item: any) => {
            if (typeof item === "object" && item !== null) {
              const label = item[targetField.labelKey] || JSON.stringify(item);
              const value = item[targetField.valueKey] || label;

              return {
                label: String(label),
                value: String(value),
                ...item, // Spread ALL API response data
                // Apply targetFields template to ALL items (defines nested API calls)
                targetFields: targetFieldsTemplate,
              };
            }
            return {
              label: String(item),
              value: String(item),
              targetFields: targetFieldsTemplate,
            };
          });

          // Store in state
          setConfigTargetFieldData((prev) => {
            const updated = {
              ...prev,
              [configValue]: {
                ...prev[configValue],
                [targetField.fieldName]: {
                  ...prev[configValue]?.[targetField.fieldName],
                  [selectedValue]: options,
                },
              },
            };

            return updated;
          });
        } else {
          console.warn(
            `No options generated for ${targetField.fieldName}. finalValue:`,
            finalValue,
          );
        }
      } catch (error: any) {
        console.error(
          ` CRITICAL ERROR in fetchConfigTargetFieldOptions for ${targetField.fieldName}:`,
          {
            error,
            errorMessage: error.message,
            errorStack: error.stack,
            configValue,
            selectedValue,
            targetField,
            bundleId: targetField.bundleId,
            variableName: targetField.variableName,
          },
        );
        console.error(` Full error object:`, error);

        // Extract meaningful error message
        let errorMessage = "An error occurred while loading options";
        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error?.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (typeof error === "string") {
          errorMessage = error;
        }

        toast({
          variant: "destructive",
          title: `Failed to load ${targetField.fieldName || "options"}`,
          description: errorMessage,
          duration: 4000,
        });
      } finally {
        // Remove from in-flight requests
        inFlightRequests.current.delete(cacheKey);
        setLoadingDropdowns((prev) => ({ ...prev, [cacheKey]: false }));

        // Decrement active API calls counter and hide loader only when all calls complete
        activeApiCallsCount.current -= 1;
        if (activeApiCallsCount.current <= 0) {
          activeApiCallsCount.current = 0; // Prevent negative values
          setLoading(false);
        }
      }
    };

    // Replace handleConfigApiOptionChange
    const handleConfigApiOptionChange = async (
      val: string,
      fieldIdx: number,
      fieldId: string,
    ) => {
      // Allow deselecting
      if (val === "select" || val === "") {
        setDropdownChains?.((prev) => {
          const updated = { ...prev };
          delete updated[fieldIdx];
          return updated;
        });
        handleFieldChange(fieldId, "");
        return;
      }

      // Store the selected configuration UID
      handleFieldChange(fieldId, val);

      //  Clear previous dropdown chains and target field data FIRST
      setDropdownChains?.((prev) => {
        const updated = { ...prev };
        updated[fieldIdx] = { 0: val }; // reset only root selection
        return updated;
      });

      // Optional: Clear form values related to this field
      setFormValues((prev) => {
        const updated = { ...prev };

        // remove any target-related values
        Object.keys(updated).forEach((key) => {
          if (key.startsWith(`${fieldId}_`)) {
            delete updated[key];
          }
        });

        return updated;
      });

      // Find the selected configuration from config_data
      const configData = meshPluginsDropdown.data?.config_data || {};
      const firstKey = Object.keys(configData)?.[0];
      const configArray = configData[firstKey] || [];
      const selectedConfig = configArray.find((c: any) => c.uid === val);

      if (!selectedConfig) {
        console.warn(" Selected configuration not found");
        return;
      }

      // Update dropdown chains with the selected config UID
      setDropdownChains?.((prev) => ({
        ...prev,
        [fieldIdx]: { 0: val },
      }));

      // Find the node in configDropdownTree that has targetFields
      const allData = Meshfields[fieldIdx];
      const tree = allData.configDropdownTree || [];

      // Find the node with targetFields (usually the root node or a default node)
      const targetNode = tree.find(
        (node: any) => node.targetFields && node.targetFields.length > 0,
      );

      if (!targetNode) {
        console.warn(" No node with targetFields found in configDropdownTree");
        return;
      }

      //  Extract ONLY first-level variables (not nested ones)
      const targetFields = targetNode.targetFields || [];

      // Only get variables from direct targetFields, not nested ones
      const firstLevelVariables = targetFields
        .filter(
          (tf: any) =>
            tf.variableName && tf.bundleId && tf.sourceType === "bundle",
        )
        .map((tf: any) => tf.variableName);

      // Update definedVariables to include the selected config
      setDefinedVariables?.((prev) => {
        const configVarName = firstKey || "selectedConfig";
        const existingVar = prev.find((v) => v.name === configVarName);

        if (existingVar) {
          return prev.map((v) =>
            v.name === configVarName ? { ...v, value: selectedConfig } : v,
          );
        } else {
          return [
            ...prev,
            {
              name: configVarName,
              value: selectedConfig,
              type: "single" as const,
              scope: "local" as const,
              sourceBundleId: 0,
              sourceRequestId: 0,
              sourcePath: "",
            },
          ];
        }
      });

      // Process ONLY first-level variables
      if (firstLevelVariables.length > 0) {
        setLoading(true);

        try {
          // Process each first-level variable one by one
          for (const varName of firstLevelVariables) {
            const variable = getCurrentDefinedVariables().find(
              (v) => v.name === varName,
            );

            if (!variable) {
              console.warn(
                ` Variable "${varName}" not found in definedVariables`,
              );
              continue;
            }

            // Call the refresh function and wait for it to complete, passing the current config value
            await refreshVariableData(variable, fieldIdx, fieldId, val);
          }

          // Fetch ONLY first-level targetField options (direct children of the config selection)
          // Nested targetFields will be fetched when their parent option is selected
          // console.log(` Fetching first-level targetFields:`, {
          //   configValue: val,
          //   targetFieldNames: targetFields.map((tf: any) => tf.fieldName),
          //   count: targetFields.length,
          // });

          for (let i = 0; i < targetFields.length; i++) {
            const targetField = targetFields[i];
            try {
              await fetchConfigTargetFieldOptions(
                val,
                val,
                targetField,
                fieldIdx,
                {}, // Empty parent selections for first level
              );
            } catch (err) {
              console.error(
                ` Error in loop for ${targetField.fieldName}:`,
                err,
              );
            }
          }

          // Force re-render
          setFormValues((prev) => ({ ...prev, _refresh: Date.now() }));
        } catch (error: any) {
          //  Clear dropdown chains completely on failure
          setDropdownChains?.((prev) => {
            const updated = { ...prev };
            delete updated[fieldIdx];
            return updated;
          });

          //  Try extracting backend error message
          const backendMessage =
            error?.response?.data?.message || // axios error response
            error?.data?.message || // custom wrapper
            error?.message || // generic JS error
            "Something went wrong";

          showToast({
            title: "Error",
            description: backendMessage,
            variant: "destructive",
            duration: 3000,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    return (
      <>
        <Dialog open={show} onOpenChange={onClose}>
          <DialogContent
            className={
              "max-h-[70vh] overflow-y-auto " +
              (showHistory ? "max-w-[60vw]" : "max-w-[20vw]")
            }
            onInteractOutside={(e) => {
              e.preventDefault();
            }}
          >
            {(meshPluginLoading || pluginLoading || loading) && (
              <div className="absolute inset-0 flex items-center justify-center z-50 rounded-lg">
                <Loader size={30} className="animate-spin" />
              </div>
            )}
            <DialogHeader>
              <DialogTitle>Plugin Action</DialogTitle>
            </DialogHeader>
            {(allActionData?.newData || []) &&
              isFeatureAllowed(ALL_KEYS.PLUGIN_ACTION_EVENTS) && (
                <div
                  className={
                    "text-right w-full mt-2 mb-2 text-xs font-bold " +
                    (Object.keys(allActionData?.newData || []).length > 0
                      ? ""
                      : "hidden")
                  }
                >
                  <Select
                    onValueChange={(e: string) => changeEvent(e)}
                    value={selectedEvent}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select the Event" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Select the event</SelectLabel>
                        {(allActionData?.newData || [])?.map((ev: any) => {
                          return (
                            <SelectItem key={ev.EventId} value={ev.EventId}>
                              {ev.EventName}
                            </SelectItem>
                          );
                        })}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              )}
            <div
              className={
                "text-right w-full mt-2 mb-2 text-xs font-bold " +
                (Object.keys(pluginData).length > 0 ? "" : "hidden")
              }
            >
              <div
                className="text-green-600 cursor-pointer inline-block"
                onClick={() => setShowHistory(!showHistory)}
              >
                <span>{showHistory ? "Hide History " : "Show History "}</span>
                <History className="inline-block ms-1" size={12} />
              </div>
            </div>

            <div
              className={
                "w-full max-h-[70vh] overflow-y-auto " +
                (showHistory ? "" : "hidden")
              }
            >
              <div className="font-bold text-lg mb-2">Action History</div>
              <DataTable cols={historyColumns} data={pluginHistory} />
            </div>

            <div className={"w-full " + (showHistory ? "hidden" : "")}>
              <form id="pluginActionViewForm" role="form" method="POST">
                <Select onValueChange={(e: string) => changePlugin(e)}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select Plugin Action" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Select Plugin Action</SelectLabel>
                      {pluginDropdown}
                    </SelectGroup>
                  </SelectContent>
                </Select>

                {!isMeshconntexSelected && (
                  <div
                    className="pluginActionTemplateDiv mt-2"
                    data-rawdata={base64EncData}
                  >
                    {pluginContent && base64EncData ? (
                      <PluginEnv
                        key={`${pluginData.id}-${base64EncData}`}
                        htmlContent={pluginContent}
                      />
                    ) : null}
                  </div>
                )}
              </form>
            </div>

            {isMeshconntexSelected && !showHistory && (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {Meshfields.map((field: any, fieldIdx: number) => {
                  const {
                    fieldId,
                    fieldTitle,
                    fieldType,
                    isAnomaly,
                    isSettings,
                    values = [],
                    placeholder,
                    // default: defaultVal,
                  } = field;

                  const currentValue =
                    formValues[fieldId] || field.defaultValue || "";

                  return (
                    <div key={fieldId} className="mb-4 mt-4">
                      {fieldType !== "submit" && (
                        <label className="block text-sm font-medium mb-1">
                          {fieldTitle}
                        </label>
                      )}

                      {/*  Handle dropdownTree (API-based nested dropdowns) */}
                      {fieldType === "dropdown" &&
                        field.dropdownTree &&
                        Array.isArray(field.dropdownTree) &&
                        field.dropdownTree.length > 0 && (
                          <div className="space-y-2">
                            {/* Root level dropdown */}
                            <Select
                              value={dropdownChains?.[fieldIdx]?.[0] || ""}
                              onValueChange={(val) =>
                                handleApiOptionChange(val, fieldIdx, fieldId)
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select an option" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectItem value="select">
                                    Select an option
                                  </SelectItem>
                                  {field.dropdownTree.map((node: any) => (
                                    <SelectItem
                                      key={node.value}
                                      value={node.value}
                                    >
                                      {node.label}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>

                            {/* Render nested levels */}
                            {(() => {
                              const selectedRootValue =
                                dropdownChains?.[fieldIdx]?.[0];
                              const selectedRootNode = field.dropdownTree.find(
                                (node: any) => node.value === selectedRootValue,
                              );

                              if (
                                selectedRootNode &&
                                selectedRootValue !== "select"
                              ) {
                                return renderDropdownTreeLevels(
                                  selectedRootNode,
                                  fieldIdx,
                                  1,
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}

                      {/*  Handle configDropdownTree (Settings-based nested dropdowns) */}
                      {fieldType === "dropdown" &&
                        field.configDropdownTree &&
                        Array.isArray(field.configDropdownTree) &&
                        field.configDropdownTree.length > 0 &&
                        (!field.dropdownTree ||
                          field.dropdownTree.length === 0) && (
                          <div className="space-y-2">
                            {/* Root level dropdown - Shows actual config data */}
                            <Select
                              value={dropdownChains?.[fieldIdx]?.[0] || ""}
                              // disabled={!checkSettingsFieldFilled()}
                              onValueChange={(val) =>
                                handleConfigApiOptionChange(
                                  val,
                                  fieldIdx,
                                  fieldId,
                                )
                              }
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue
                                  placeholder={
                                    !checkSettingsFieldFilled() &&
                                    "Select a configuration"
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectGroup>
                                  <SelectItem value="select">
                                    Select an option
                                  </SelectItem>
                                  {(() => {
                                    //  Get configuration data from meshPluginsDropdown
                                    const configData =
                                      meshPluginsDropdown.data?.config_data ||
                                      {};
                                    const firstKey =
                                      Object.keys(configData)?.[0];
                                    const configArray =
                                      configData[firstKey] || [];

                                    if (configArray.length === 0) {
                                      return (
                                        <SelectItem value="none" disabled>
                                          No configuration data found
                                        </SelectItem>
                                      );
                                    }

                                    // Map configuration data to dropdown options
                                    return configArray.map(
                                      (conf: any, i: number) => (
                                        <SelectItem
                                          key={conf.uid || i}
                                          value={conf.uid || String(i)}
                                        >
                                          {conf.title ||
                                            conf.name ||
                                            `Configuration ${i + 1}`}
                                        </SelectItem>
                                      ),
                                    );
                                  })()}
                                </SelectGroup>
                              </SelectContent>
                            </Select>

                            {/* Render nested config levels AFTER API call */}
                            {(() => {
                              const selectedRootValue =
                                dropdownChains?.[fieldIdx]?.[0];

                              // Only show nested levels if a valid config is selected
                              if (
                                !selectedRootValue ||
                                selectedRootValue === "select" ||
                                selectedRootValue === ""
                              ) {
                                return null;
                              }

                              // Find the matching node in configDropdownTree
                              // The node should have targetFields that will be populated by API
                              const selectedRootNode =
                                field.configDropdownTree.find((node: any) => {
                                  // Match by any criteria since we're using config UID
                                  return (
                                    node.value === selectedRootValue ||
                                    node.label === selectedRootValue ||
                                    node.value === "config_root" ||
                                    node.isDefaultOption === true
                                  );
                                });

                              // If no specific node matches, use the first node with targetFields
                              const targetNode =
                                selectedRootNode ||
                                field.configDropdownTree.find(
                                  (node: any) =>
                                    node.targetFields &&
                                    node.targetFields.length > 0,
                                );

                              if (
                                targetNode &&
                                targetNode.targetFields &&
                                targetNode.targetFields.length > 0
                              ) {
                                return renderDropdownTreeLevels(
                                  targetNode,
                                  fieldIdx,
                                  1,
                                );
                              }

                              return null;
                            })()}
                          </div>
                        )}

                      {/* Anomaly Dropdown */}
                      {fieldType === "dropdown" &&
                        isAnomaly &&
                        (!field.dropdownTree ||
                          field.dropdownTree.length === 0) &&
                        (!field.configDropdownTree ||
                          field.configDropdownTree.length === 0) && (
                          <Select
                            value={currentValue}
                            onValueChange={(val) =>
                              handleFieldChange(
                                fieldId,
                                val === "select" ? "" : val,
                              )
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select Entity Value" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="select">
                                Select an option
                              </SelectItem>
                              {(() => {
                                const anomalyTypeKeys =
                                  getAnomalyTypeKeysFromField(field);
                                let options: {
                                  label: string;
                                  value: string;
                                }[] = [];

                                if (
                                  (entityData &&
                                    typeof entityData === "object") ||
                                  (allActionData &&
                                    typeof allActionData === "object")
                                ) {
                                  const entries = collectAnomalyEntityEntries(
                                    entityData,
                                    allActionData,
                                  );

                                  if (anomalyTypeKeys.includes("string")) {
                                    options = entries.map(([key, value]) => ({
                                      label: `${key} - ${value}`,
                                      value: String(value),
                                    }));
                                  } else {
                                    const validKeys = anomalyTypeKeys.flatMap(
                                      (typeKey) =>
                                        ENTITY_CATEGORY_MAP[typeKey] || [],
                                    );
                                    const normalize = (text: string) =>
                                      text
                                        .toLowerCase()
                                        .replace(/[^a-z0-9]/g, "");
                                    const validKeySet = new Set(
                                      validKeys.map((vkey) => normalize(vkey)),
                                    );
                                    const matchedEntries = entries.filter(
                                      ([key]) =>
                                        validKeySet.has(normalize(key)),
                                    );

                                    options = matchedEntries.map(
                                      ([key, value]) => ({
                                        label: `${key} - ${value}`,
                                        value: String(value),
                                      }),
                                    );
                                  }
                                }

                                // Remove duplicates caused by the same value appearing in
                                // multiple anomaly payload keys (e.g., Query/query/display.Query).
                                const seenValues = new Set<string>();
                                options = options.filter((opt) => {
                                  const normalizedValue = String(opt.value)
                                    .trim()
                                    .toLowerCase();
                                  if (!normalizedValue) return false;
                                  if (seenValues.has(normalizedValue)) {
                                    return false;
                                  }
                                  seenValues.add(normalizedValue);
                                  return true;
                                });

                                if (options.length === 0)
                                  return (
                                    <SelectItem
                                      value="no_matching_fields"
                                      disabled
                                    >
                                      No field is matching
                                    </SelectItem>
                                  );

                                return options.map((opt, i) => (
                                  <SelectItem key={i} value={opt.value}>
                                    {opt.label}
                                  </SelectItem>
                                ));
                              })()}
                            </SelectContent>
                          </Select>
                        )}

                      {/* Settings Dropdown */}
                      {isSettings && (
                        <Select
                          value={currentValue}
                          onValueChange={(val) =>
                            handleFieldChange(
                              fieldId,
                              val === "select" ? "" : val,
                            )
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select configuration" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="select">
                              Select configuration
                            </SelectItem>
                            {(() => {
                              const configData =
                                meshPluginsDropdown.data?.config_data || {};
                              const firstKey = Object.keys(configData)?.[0];
                              const configArray = configData[firstKey] || [];

                              if (configArray.length === 0)
                                return (
                                  <SelectItem value="none">
                                    No configuration data found
                                  </SelectItem>
                                );

                              return configArray.map((conf: any, i: number) => (
                                <SelectItem key={i} value={conf.uid}>
                                  {conf.title || conf.name || `Config ${i + 1}`}
                                </SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                      )}

                      {/* Regular Dropdown (no trees) */}
                      {fieldType === "dropdown" &&
                        !isAnomaly &&
                        !isSettings &&
                        (!field.dropdownTree ||
                          field.dropdownTree.length === 0) &&
                        (!field.configDropdownTree ||
                          field.configDropdownTree.length === 0) && (
                          <Select
                            value={currentValue}
                            onValueChange={(val) =>
                              handleFieldChange(
                                fieldId,
                                val === "select" ? "" : val,
                              )
                            }
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue
                                placeholder={placeholder || "Select an option"}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="select">
                                Select an option
                              </SelectItem>
                              {values?.length ? (
                                values.map((val: any, j: number) => {
                                  const labelKey =
                                    field.userAction?.mapResponse?.label;
                                  const valueKey =
                                    field.userAction?.mapResponse?.value;

                                  let label = labelKey
                                    ? val[labelKey]
                                    : val.displayName || val.label || val;
                                  let value = valueKey
                                    ? val[valueKey]
                                    : val.value || val.displayName || String(j);

                                  if (typeof label === "object")
                                    label = JSON.stringify(label);
                                  if (typeof value === "object")
                                    value = JSON.stringify(value);

                                  return (
                                    <SelectItem key={j} value={value}>
                                      {label}
                                    </SelectItem>
                                  );
                                })
                              ) : (
                                <SelectItem value="none">
                                  No dropdown options
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        )}

                      {/* Number Input */}
                      {fieldType === "input_number" && (
                        <Input
                          type="number"
                          placeholder={placeholder || "Enter number"}
                          value={currentValue}
                          onChange={(e) =>
                            handleFieldChange(fieldId, e.target.value)
                          }
                          className="w-full"
                        />
                      )}

                      {/* Text Input */}
                      {fieldType === "string" && (
                        <Input
                          type="text"
                          placeholder={placeholder || "Enter text"}
                          value={currentValue}
                          onChange={(e) =>
                            handleFieldChange(fieldId, e.target.value)
                          }
                          className="w-full"
                        />
                      )}

                      {/* Default Input */}
                      {![
                        "dropdown",
                        "input_number",
                        "string",
                        "submit",
                        "import_settings",
                      ].includes(fieldType) && (
                        <Input
                          type="text"
                          placeholder={placeholder || "Enter value"}
                          value={currentValue}
                          onChange={(e) =>
                            handleFieldChange(fieldId, e.target.value)
                          }
                          className="w-full"
                        />
                      )}
                    </div>
                  );
                })}

                {/* Submit Button */}
                <Button type="submit" className="mt-6">
                  Submit
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.show == nextProps.show;
  },
);

PluginActionDialog.displayName = "PluginActionDialog";
export default PluginActionDialog;
